/**
 * Pure mutation functions for the stored quote document HTML.
 *
 * The stored HTML string is the single source of truth. Every function here
 * parses it with DOMParser, applies exactly one structural mutation on the
 * parsed DOM (never string surgery), and re-serializes the body back to the
 * stored form. Each mutation returns the new HTML together with the positional
 * node id ("nK" = index among body element children, matching the ids the
 * renderer stamps at parse time) of the affected block, so callers can keep
 * the selection on the edited block even when ids shift.
 *
 * The functions are framework-free and fully unit-testable. The inline
 * text-edit commit path (mirroring rendered contenteditable edits back to
 * source) lives in renderedTextEdit.ts.
 */

import {
  FP_LINES_FILTERS,
  FP_SHOW_WHEN_ATTRIBUTE,
  FP_TAGS,
  type FpContainerAttribute,
  normalizeSelfClosingFpTags,
  type FpLinesFilter,
} from "@forgepoint/shared";
import { directChildren, parseTableColumn } from "./fpTableColumns";
import { lineFieldLabel } from "./lineFields";
import { quoteFieldLabel, SIGNATURE_FONT_CLASS } from "./quoteFields";

/**
 * The style knobs the properties panel can write onto a block. Every entry must
 * kebab-case into the shared `ALLOWED_STYLE_PROPERTIES` allowlist, or the
 * sanitizer strips the knob's output on save.
 */
export const STYLE_PROPERTIES = [
  "backgroundColor",
  "borderRadius",
  "color",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "margin",
  "padding",
  "textAlign",
  "textDecoration",
] as const;

/** One of the editable inline-style properties. */
export type StyleProperty = (typeof STYLE_PROPERTIES)[number];

/** The CSS property names the style knobs write, checked against the allowlist. */
export const STYLE_PROPERTY_CSS_NAMES: readonly string[] =
  STYLE_PROPERTIES.map(toKebabCase);

/**
 * The container attribute duplicating the font-size knob. Authored documents
 * set a block's type size with it far more often than with an inline style, so
 * the knob reads it as a fallback and drops it the moment it writes, leaving
 * one authoritative channel behind.
 */
const FONT_SIZE_ATTRIBUTE = "size";

/**
 * The container attributes duplicating a type-style knob's channel. Authored
 * documents carry whole-block emphasis as these props (`weight="700"`,
 * `decoration="underline"`), so each knob reads its prop as a fallback and
 * drops it the moment it writes - without this, the bold and underline
 * toggles would toggle an inline style underneath a prop that keeps winning.
 */
const TYPE_STYLE_ATTRIBUTES: Partial<
  Record<StyleProperty, FpContainerAttribute>
> = {
  fontSize: FONT_SIZE_ATTRIBUTE,
  fontWeight: "weight",
  fontStyle: "font-style",
  textDecoration: "decoration",
};

/** The tag declaring one column of an `<fp-table>`. */
const COLUMN_TAG = "fp-column";

/**
 * The fp container attribute each style knob writes on an `<fp-column>`. A
 * column's rendered cells take their look from its container attributes alone,
 * so an inline style written there would never reach the table cell.
 */
const COLUMN_STYLE_ATTRIBUTES: Record<StyleProperty, FpContainerAttribute> = {
  backgroundColor: "background",
  borderRadius: "radius",
  color: "color",
  fontSize: "size",
  fontStyle: "font-style",
  fontWeight: "weight",
  margin: "margin",
  padding: "padding",
  textAlign: "align",
  textDecoration: "decoration",
};

/** Rendered tags that can host an inline text edit. */
export const TEXT_EDITABLE_TAGS = new Set([
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "P",
  "LI",
  "UL",
  "OL",
  "TD",
  "TH",
  "BLOCKQUOTE",
  "FIGCAPTION",
  "SPAN",
]);

const TEXT_EDITABLE_SELECTOR = [...TEXT_EDITABLE_TAGS].join(",").toLowerCase();

/** The result of a document mutation: the new HTML and the id to reselect. */
export interface DocumentEdit {
  /** The re-serialized document HTML after the mutation. */
  html: string;
  /** Positional node id of the affected block after the mutation, if any. */
  nodeId: string | null;
}

/** Inspector-facing description of one top-level source block. */
export interface BlockInfo {
  /** The block's positional node id. */
  nodeId: string;
  /** Lowercased tag name (e.g. "p", "fp-markdown"). */
  tagName: string;
  /** Short human-readable label for the selection chip. */
  label: string;
  /** Authored values for the editable style properties ("" = unset). */
  styles: Record<StyleProperty, string>;
  /** The `size` container attribute, which the font-size style overrides. */
  containerSize: string | null;
  /** The `source` attribute for a field-bound block, else null. */
  fieldSource: string | null;
  /** The `asset-id` attribute for fp-image blocks, else null. */
  imageAssetId: string | null;
  /** The `height` attribute (in px) for fp-image blocks, else null. */
  imageHeight: string | null;
  /** The `width` attribute (in px) for fp-image blocks, else null. */
  imageWidth: string | null;
  /** The `data-fp-show-when` condition gating the block, or null. */
  showWhen: string | null;
  /**
   * True when the block starts a page region, making it the block whose
   * `data-fp-skip-footer` decides that page's footer. Only a page break or the
   * first block after one qualifies - the renderer ignores the attribute
   * anywhere else, so the inspector must not offer it there.
   */
  opensPageRegion: boolean;
  /** True when the block carries `data-fp-skip-header`. */
  skipsHeader: boolean;
  skipsFirstHeader: boolean;
  /** True when the block carries `data-fp-skip-footer`. */
  skipsFooter: boolean;
  /**
   * True when the block IS the authored repeatable header or footer. There is
   * exactly one of each and both are lifted out of the document flow at render
   * time, so moving and duplicating them are meaningless and the inspector
   * withholds them.
   */
  isRepeatableBlock: boolean;
  /**
   * True when the block renders on every page carrying it - the repeatable
   * header or footer, or anything inside one. One source id then has one
   * rendered instance per page, which per-instance canvas chrome has to
   * account for.
   */
  repeatsOnEveryPage: boolean;
  /**
   * Enclosing selectable blocks, outermost first. A nested block's container is
   * often covered edge to edge by its own children, so the breadcrumb is the way
   * back up to the element carrying the layout and the show-when condition.
   */
  ancestors: BlockBreadcrumb[];
  /**
   * Editable info when the selected block is a repeater (fp-lines, fp-breaks),
   * or null for other blocks. The repeater inspector drives off this; stamped
   * canvas instances stay read-only projections.
   */
  repeater: RepeaterInfo | null;
  /**
   * Editable info when the selection sits in a static table - an `<fp-table>`
   * with authored `<fp-row>` children, or one of those rows or cells - else
   * null. The table inspector's row and column operations drive off this.
   */
  table: TableInfo | null;
  /**
   * The enclosing `<fp-table>`'s positional node id when the selection is a
   * table or one of its columns, rows or cells - line-bound tables included -
   * else null. The column grips drive off this, so it is resolved through the
   * source tree rather than by trimming the selection's id: an `<fp-columns>`
   * column is an `<fp-cell>` too, and trimming would name an unrelated element.
   */
  tableNodeId: string | null;
  /**
   * Editable info when the selection is an `<fp-columns>` row or sits inside
   * one, else null. The columns grips and the add-column operation drive off
   * this.
   */
  columns: ColumnsInfo | null;
  /**
   * True when the block renders as a `<td>`, where CSS margin does nothing. An
   * `<fp-cell>` is a table cell inside a table and a plain box anywhere else,
   * so only where it sits decides which box props the panel can offer.
   */
  rendersAsTableCell: boolean;
  /** True when the block holds authored text a double-click can edit inline. */
  hasEditableText: boolean;
  /** True when the block carries the authored-signature font class. */
  usesSignatureFont: boolean;
}

/** Editable info for a selected repeater block. */
export interface RepeaterInfo {
  /** The repeater tag: "fp-lines" or "fp-breaks". */
  tag: string;
  /** The `filter` attribute of an fp-lines repeater, or null. */
  filter: string | null;
  /** The `<template>` inner HTML for the repeater, or null when it has none. */
  templateInnerHtml: string | null;
  /** Nested repeater child blocks, for drilling down. */
  children: BlockBreadcrumb[];
}

/** What the table inspector can do for a selection inside a static table. */
export interface TableInfo {
  /** True when a row anchor is derivable: the selection is a row or a cell. */
  canEditRows: boolean;
  /** True when deleting the anchor row still leaves the table a row. */
  canRemoveRow: boolean;
  /** True when a column anchor is derivable: a cell without `span="all"`. */
  canEditColumns: boolean;
  /** True when deleting the anchor column still leaves the table a column. */
  canRemoveColumn: boolean;
  /** True per direction when the anchor cell has a matching cell to merge. */
  canMergeLeft: boolean;
  canMergeRight: boolean;
  canMergeUp: boolean;
  canMergeDown: boolean;
  /** True when the anchor cell is a merge that can be split back apart. */
  canUnmerge: boolean;
}

/** The direction a table cell merges into its neighbour. */
export type MergeDirection = "left" | "right" | "up" | "down";

/** Editable info for a selection in an `<fp-columns>` row. */
export interface ColumnsInfo {
  /** The enclosing `<fp-columns>` element's positional node id. */
  nodeId: string;
}

/** One breadcrumb entry pointing at a selectable block. */
export interface BlockBreadcrumb {
  /** The block's positional node id. */
  nodeId: string;
  /** A short label (tag plus its filter/source hint). */
  label: string;
}

/**
 * Parses the stored document HTML into an inert DOM document. Self-closing
 * `fp-*` tags are expanded first: the editor serializes this document straight
 * back to storage, so an unclosed tag would otherwise persist every following
 * block as its child.
 * @param html - The stored document HTML
 * @returns The parsed document
 */
export function parseDocumentHtml(html: string): Document {
  return new DOMParser().parseFromString(
    normalizeSelfClosingFpTags(html),
    "text/html",
  );
}

/**
 * Serializes a parsed source document back to the stored HTML form.
 * @param doc - The parsed source document
 * @returns The body's inner HTML (the stored document format)
 */
export function serializeDocumentHtml(doc: Document): string {
  return doc.body.innerHTML;
}

/**
 * Reads the inspector description of a block: tag, label, editable styles, and
 * the fp-specific attributes the properties panel can edit. The style values
 * come from whichever channel renders the block - inline styles, or the mapped
 * container attributes for an `<fp-table>` column.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block
 * @returns The block info, or null when the id doesn't resolve
 */
export function getBlockInfo(html: string, nodeId: string): BlockInfo | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const tagName = block.tagName.toLowerCase();
  const styles: Record<StyleProperty, string> = {
    backgroundColor: "",
    borderRadius: "",
    color: "",
    fontSize: "",
    fontStyle: "",
    fontWeight: "",
    margin: "",
    padding: "",
    textAlign: "",
    textDecoration: "",
  };
  STYLE_PROPERTIES.forEach((property) => {
    if (tagName === COLUMN_TAG) {
      styles[property] =
        block.getAttribute(COLUMN_STYLE_ATTRIBUTES[property]) ?? "";
      return;
    }
    const typeAttribute = TYPE_STYLE_ATTRIBUTES[property];
    styles[property] =
      block.style.getPropertyValue(toKebabCase(property)) ||
      (typeAttribute === undefined
        ? ""
        : (block.getAttribute(typeAttribute) ?? ""));
  });
  const repeater = summarizeRepeater(doc, block);
  return {
    nodeId,
    tagName,
    label: describeBlock(block),
    styles,
    containerSize: block.getAttribute(FONT_SIZE_ATTRIBUTE),
    fieldSource: FIELD_BOUND_TAGS.has(tagName)
      ? block.getAttribute("source")
      : null,
    imageAssetId:
      tagName === "fp-image" ? block.getAttribute("asset-id") : null,
    imageHeight: tagName === "fp-image" ? block.getAttribute("height") : null,
    imageWidth: tagName === "fp-image" ? block.getAttribute("width") : null,
    showWhen: block.getAttribute(FP_SHOW_WHEN_ATTRIBUTE),
    opensPageRegion: opensPageRegion(doc, block),
    skipsHeader: block.hasAttribute(FP_SKIP_HEADER_ATTRIBUTE),
    skipsFirstHeader: block.hasAttribute(FP_SKIP_FIRST_HEADER_ATTRIBUTE),
    skipsFooter: block.hasAttribute(FP_SKIP_FOOTER_ATTRIBUTE),
    isRepeatableBlock: isRepeatableBlock(block),
    repeatsOnEveryPage: insideRepeatableBlock(block),
    ancestors: collectAncestors(doc, block),
    repeater,
    table: summarizeTable(block),
    tableNodeId: enclosingTableNodeId(doc, block),
    columns: summarizeColumns(doc, block),
    rendersAsTableCell: rendersAsTableCell(block),
    hasEditableText: repeater === null && hasAuthoredTextContent(block),
    usesSignatureFont: block.classList.contains(SIGNATURE_FONT_CLASS),
  };
}

/** Attribute hiding the repeatable header on a page region. */
export const FP_SKIP_HEADER_ATTRIBUTE = "data-fp-skip-header";

/** Attribute hiding the repeatable header only on a region's first page. */
export const FP_SKIP_FIRST_HEADER_ATTRIBUTE = "data-fp-skip-first-header";

/** Attribute hiding the repeatable footer on a page region. */
export const FP_SKIP_FOOTER_ATTRIBUTE = "data-fp-skip-footer";

/**
 * Whether a block starts a page region, and so decides whether the repeatable
 * header and footer show on that page. Mirrors the renderer's region splitting:
 * a page break opens a region, as does the first block of the run that follows
 * one. A block anywhere else in a region has no say - the renderer never reads
 * its skip attributes.
 * @param doc - The parsed source document
 * @param block - The block to test
 * @returns True when the block opens a page region
 */
function opensPageRegion(doc: Document, block: Element): boolean {
  if (block.parentElement !== doc.body || isLiftedFromFlow(block)) {
    return false;
  }
  if (block.tagName.toLowerCase() === "fp-page-break") {
    return true;
  }
  const previous = previousRegionBlock(block);
  return (
    previous === null || previous.tagName.toLowerCase() === "fp-page-break"
  );
}

/**
 * The nearest preceding top-level sibling that takes part in region splitting.
 * Blocks lifted out of the flow before regions are collected never separate a
 * block from the page break before it.
 * @param block - The block to walk back from
 * @returns The preceding block, or null when the block is first
 */
function previousRegionBlock(block: Element): Element | null {
  for (
    let sibling = block.previousElementSibling;
    sibling !== null;
    sibling = sibling.previousElementSibling
  ) {
    if (!isLiftedFromFlow(sibling)) {
      return sibling;
    }
  }
  return null;
}

/**
 * Whether the renderer lifts a block out of the page flow before splitting
 * regions: the repeatable header and footer, and the page frame. None of them
 * opens a region, so none carries the header/footer skip attributes.
 * @param element - The block to test
 * @returns True when the block never takes part in region splitting
 */
function isLiftedFromFlow(element: Element): boolean {
  return (
    isRepeatableBlock(element) ||
    element.tagName.toLowerCase() === "fp-page-frame"
  );
}

/** The tags a selected block reports as a repeater. */
const REPEATER_INFO_TAGS = new Set(["fp-lines", "fp-breaks"]);

/** The parents that make an `<fp-cell>` a `<td>` rather than a box of its own. */
const TABLE_CELL_PARENT_TAGS = new Set(["fp-row", "fp-table"]);

/**
 * Whether a block renders as a table cell. `<fp-table>` reads its own row and
 * cell children rather than converting them, so an `<fp-cell>` is a `<td>` only
 * when a table part encloses it; one reaching the converter draws its own box.
 * @param element - The selected source element
 * @returns True when the element renders as a `<td>`
 */
function rendersAsTableCell(element: Element): boolean {
  if (element.tagName.toLowerCase() !== "fp-cell") {
    return false;
  }
  const parent = element.parentElement?.tagName.toLowerCase() ?? "";
  return TABLE_CELL_PARENT_TAGS.has(parent);
}

/**
 * fp-* tags that never select on their own. A field, a line field and a page
 * number are inline data slots with no box of their own, so the primitive
 * wrapping one is what gets selected; a page break renders no element at all
 * and is selected through its canvas marker instead.
 */
const FP_UNSELECTABLE_TAGS = new Set<string>([
  "fp-field",
  "fp-line-field",
  "fp-page-number",
  "fp-page-count",
  "fp-page-break",
]);

/**
 * Every fp-* primitive selectable in its own right when nested. Each one carries
 * the shared container props (padding, border, width, align, size, ...), so each
 * is a box worth authoring rather than a structural wrapper, and each is
 * selectable whether or not it holds other primitives.
 */
const FP_SELECTABLE_TAGS: ReadonlySet<string> = new Set<string>(
  FP_TAGS.filter((tag) => !FP_UNSELECTABLE_TAGS.has(tag)),
);

/**
 * Standard HTML block tags selectable when nested inside a top-level block, and
 * only as the innermost block of their subtree - they carry nothing of their own
 * to author, so an enclosing one would only compete for its child's pixels.
 * Inline tags are deliberately absent, as are lists: stamping `ul`/`ol`/`li`
 * would stop the inline text editor's climb to the list root, breaking
 * Tab-indent.
 */
const LEGACY_NESTED_BLOCK_TAGS = new Set([
  "blockquote",
  "div",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "p",
  "section",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
]);

/** Every tag that can be stamped as a nested selectable block. */
const NESTED_BLOCK_TAGS: ReadonlySet<string> = new Set<string>([
  ...LEGACY_NESTED_BLOCK_TAGS,
  ...FP_SELECTABLE_TAGS,
]);

/**
 * Whether a nested element is selectable as its own editor block: a repeater,
 * any fp-* primitive, or the innermost standard HTML block of its subtree.
 * Natural DOM hit-testing then decides what a click selects - a cell's padding
 * or border targets the cell, its text targets the text inside - and the
 * breadcrumb walks up to the enclosing containers from either.
 *
 * The renderer's `data-fp-node` stamping and the breadcrumb's ancestor walk both
 * derive from this, so what the canvas offers and what the inspector links to
 * cannot drift apart.
 *
 * An `fp-field` is selectable only when it stands alone (its parent is not a
 * text-flow element): a standalone field inside a section is its own block,
 * while a field flowing inline in a paragraph keeps selecting with the
 * paragraph, whose inline edit session owns it.
 * @param element - The nested source element to test
 * @returns True when the element is selectable on its own
 */
export function isNestedSelectableBlock(element: Element): boolean {
  const tag = element.tagName.toLowerCase();
  if (REPEATER_INFO_TAGS.has(tag) || FP_SELECTABLE_TAGS.has(tag)) {
    return true;
  }
  return LEGACY_NESTED_BLOCK_TAGS.has(tag) && !hasSelectableDescendant(element);
}

/**
 * Whether an element's subtree holds a selectable block, making the element a
 * container rather than the innermost block. `<template>` interiors are skipped:
 * they are authored through the enclosing repeater, never selected on canvas.
 * @param element - The element whose subtree is scanned
 * @returns True when a descendant is selectable
 */
function hasSelectableDescendant(element: Element): boolean {
  return Array.from(element.children).some((child) => {
    const tag = child.tagName.toLowerCase();
    if (tag === "template") {
      return false;
    }
    if (REPEATER_INFO_TAGS.has(tag) || NESTED_BLOCK_TAGS.has(tag)) {
      return true;
    }
    return hasSelectableDescendant(child);
  });
}

/**
 * Whether a source block is or contains an element that can host an inline
 * text edit. fp-derived content contributes nothing here: fp-field/fp-image
 * are empty in source and repeater interiors live inside `<template>`, which
 * querySelector does not descend into.
 * @param block - The top-level source block
 * @returns True when a double-click somewhere in the block can edit text
 */
function hasAuthoredTextContent(block: Element): boolean {
  return (
    block.matches(TEXT_EDITABLE_SELECTOR) ||
    block.querySelector(TEXT_EDITABLE_SELECTOR) !== null
  );
}

/**
 * Builds the editable repeater info when the selected block is itself a
 * repeater (fp-lines, fp-breaks), or null otherwise. Nested
 * repeaters carry their own node id, so a repeater is always selected as the
 * element itself rather than an enclosing flat block.
 * @param doc - The parsed source document
 * @param block - The selected source element
 * @returns The repeater info, or null for non-repeater blocks
 */
function summarizeRepeater(doc: Document, block: Element): RepeaterInfo | null {
  const tag = block.tagName.toLowerCase();
  if (!REPEATER_INFO_TAGS.has(tag)) {
    return null;
  }
  const template = directTemplateOf(block);
  return {
    tag,
    filter: block.getAttribute("filter"),
    templateInnerHtml: template ? template.innerHTML : null,
    children: collectRepeaterChildren(doc, block),
  };
}

/**
 * The direct `<template>` child of a repeater, or null.
 * @param block - The repeater element
 * @returns The template child, or null
 */
function directTemplateOf(block: Element): HTMLTemplateElement | null {
  return (
    Array.from(block.children).find(
      (child): child is HTMLTemplateElement =>
        child instanceof HTMLTemplateElement,
    ) ?? null
  );
}

/**
 * Collects the selectable ancestors of a block, outermost first, for the
 * properties-panel breadcrumb. Only stamped elements qualify - a top-level block
 * or a nested selectable one - so every crumb resolves to a block the canvas can
 * actually highlight.
 * @param doc - The parsed source document
 * @param block - The selected source element
 * @returns The ancestor breadcrumb entries, outermost first
 */
function collectAncestors(doc: Document, block: Element): BlockBreadcrumb[] {
  const ancestors: BlockBreadcrumb[] = [];
  for (
    let element = block.parentElement;
    element !== null && element !== doc.body;
    element = element.parentElement
  ) {
    if (
      element.parentElement === doc.body ||
      isNestedSelectableBlock(element)
    ) {
      ancestors.unshift(breadcrumbOf(doc, element));
    }
  }
  return ancestors;
}

/**
 * Collects the nearest nested repeater descendants reachable in
 * FLAT markup (outside any `<template>`), matching the ids the renderer stamps
 * on the canvas. Repeaters that live inside a `<template>` (e.g. an fp-breaks
 * inside an fp-lines template) are not canvas-selectable and are edited through
 * the enclosing repeater's column editor instead.
 * @param doc - The parsed source document
 * @param block - The selected repeater element
 * @returns The nested flat breadcrumb entries in document order
 */
function collectRepeaterChildren(
  doc: Document,
  block: Element,
): BlockBreadcrumb[] {
  const children: BlockBreadcrumb[] = [];
  const visit = (element: Element) => {
    Array.from(element.children).forEach((child) => {
      if (child instanceof HTMLTemplateElement) {
        return;
      }
      if (REPEATER_INFO_TAGS.has(child.tagName.toLowerCase())) {
        children.push(breadcrumbOf(doc, child));
        return;
      }
      visit(child);
    });
  };
  visit(block);
  return children;
}

/**
 * Builds a breadcrumb entry for a repeater element.
 * @param doc - The parsed source document
 * @param element - The repeater element
 * @returns The breadcrumb entry
 */
function breadcrumbOf(doc: Document, element: Element): BlockBreadcrumb {
  return { nodeId: blockIdOf(doc, element), label: describeBlock(element) };
}

/** Which side of an anchor block a new block is inserted on. */
export type BlockInsertPosition = "before" | "after";

/**
 * The block that hover chrome acts on for a given block: itself for an ordinary
 * flow block, and the enclosing table for anything laid out as grid content.
 * Inserting and reordering only ever act on whole flow blocks - a block dropped
 * beside a table column renders squeezed or not at all - so pointing the grip
 * and the "+" buttons at one would offer operations that land somewhere else.
 * An `<fp-columns>` column is not such a case: it lays out in normal flow, so
 * the chrome stays on the block inside the column and its "+" buttons insert
 * there. This is the same climb an insert makes, so what the chrome hangs off is
 * what it acts on.
 * @param html - The stored document HTML
 * @param nodeId - The hovered block's node id
 * @returns The node id chrome should hang off, or the input when unresolvable
 */
export function chromeAnchorNodeId(html: string, nodeId: string): string {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  return block ? blockIdOf(doc, outermostGridBoxOf(block)) : nodeId;
}

/**
 * Inserts an authored block on the given side of the anchor block, or at the
 * end of the document (before the repeatable footer when present) when no
 * anchor is given. The repeatable footer is not a usable anchor: it is lifted
 * out of the flow at render time and stays last in source, so an insert
 * anchored on it falls back to the end-of-document position, keeping source
 * order and render order in step. The repeatable header IS a usable anchor: it
 * stays first in source, so inserting after it lands at the top of the document
 * flow. A page break anchored on a NESTED block splits the anchor's top-level
 * ancestor in two at the anchor - page breaks only act between top-level
 * blocks, so a nested insert would silently do nothing. The break then sits
 * between the halves, exactly where the user was pointing. An insert aimed at a
 * whole `<fp-columns>` column lands INSIDE that column, stacked with what it
 * already holds, so a column can hold any block rather than only the one it was
 * authored with; adding a column is a separate operation (addColumnsColumn).
 * An inserted repeatable header or footer ignores the anchor entirely: both are
 * lifted out of the flow at render time, so the header lands first in source
 * and the footer last, and a second one is refused - the renderer only ever
 * uses the first.
 * An insert aimed at an EMPTY repeatable header or footer lands INSIDE it:
 * an empty chrome block offers nothing else to aim at, and filling it is the
 * only way to build one back up from the editor's placeholder. A page break is
 * the exception - it only acts between top-level blocks, so it lands beside
 * the chrome like any other anchor.
 * @param html - The stored document HTML
 * @param anchorNodeId - Node id to insert beside, or null for end-of-document
 * @param blockHtml - The authored HTML of the new block (single root element)
 * @param position - Which side of the anchor the block lands on
 * @returns The edit with the new block selected, or null when invalid
 */
export function insertBlockAt(
  html: string,
  anchorNodeId: string | null,
  blockHtml: string,
  position: BlockInsertPosition = "after",
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const template = doc.createElement("template");
  template.innerHTML = blockHtml.trim();
  const block = template.content.firstElementChild;
  if (!block) {
    return null;
  }
  if (isRepeatableBlock(block)) {
    return insertPageChrome(doc, block);
  }
  const resolvedAnchor = anchorNodeId ? resolveBlock(doc, anchorNodeId) : null;
  if (
    resolvedAnchor &&
    block.tagName.toLowerCase() === "fp-page-break" &&
    resolvedAnchor.parentElement !== doc.body
  ) {
    return insertPageBreakSplittingAncestors(
      doc,
      resolvedAnchor,
      block,
      position,
    );
  }
  if (
    resolvedAnchor &&
    isEmptyRepeatableBlock(resolvedAnchor) &&
    block.tagName.toLowerCase() !== "fp-page-break"
  ) {
    resolvedAnchor.append("\n", block, "\n");
    return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
  }
  const anchor =
    resolvedAnchor && isRepeatableFooter(resolvedAnchor)
      ? null
      : resolvedAnchor;
  if (
    anchor &&
    isEmptyCell(anchor) &&
    (!rendersAsTableCell(anchor) ||
      CELL_TEXT_TAGS.has(block.tagName.toLowerCase()))
  ) {
    const body = rendersAsTableCell(anchor) ? cellBodyOf(block) : null;
    if (body !== null) {
      anchor.textContent = body;
      return {
        html: serializeDocumentHtml(doc),
        nodeId: blockIdOf(doc, anchor),
      };
    }
    anchor.append(block);
    return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
  }
  if (anchor) {
    const flowAnchor = flowAnchorFor(anchor, block);
    if (isColumnsColumn(flowAnchor)) {
      placeInColumn(doc, flowAnchor, block, position);
    } else if (position === "before") {
      flowAnchor.before(block, "\n");
    } else {
      flowAnchor.after("\n", block);
    }
  } else {
    insertBeforeFooterOrAppend(doc, [block]);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
}

/**
 * Places a repeatable header first in source or a repeatable footer last,
 * keeping source order in step with where the renderer lifts them from.
 * @param doc - The parsed source document
 * @param block - The header or footer element to insert
 * @returns The edit with the chrome selected, or null when one already exists
 */
function insertPageChrome(doc: Document, block: Element): DocumentEdit | null {
  const kind = isRepeatableChrome(block, "header") ? "header" : "footer";
  if (findRepeatableChrome(doc, kind)) {
    return null;
  }
  if (kind === "header") {
    doc.body.prepend(block, "\n");
  } else {
    doc.body.append("\n", block);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
}

/**
 * The blocks a table cell holds: cells are text-only, not composable, so only
 * a text block or a bound value fills an empty one; any other block climbs out
 * of the table like every other grid anchor. A columns-column `<fp-cell>` is a
 * composable stack, not a table cell, so it stays open to any block.
 */
const CELL_TEXT_TAGS = new Set(["fp-text", "fp-field", "fp-markdown"]);

/**
 * The bare text a block dropped into an empty table cell becomes: a cell owns
 * its text rather than nesting blocks, so an authored text block contributes
 * its body and a bound field its inline `{{ source }}` token. Null for blocks
 * with no bare-text form (a bound fp-markdown), which stay element children.
 * @param block - The block aimed at the cell
 * @returns The cell's new text body, or null when the block has none
 */
function cellBodyOf(block: Element): string | null {
  const tag = block.tagName.toLowerCase();
  if (tag === "fp-text" && !block.hasAttribute("source")) {
    return block.textContent ?? "";
  }
  if (tag === "fp-field") {
    return `{{ ${block.getAttribute("source") ?? ""} }}`;
  }
  return null;
}

/**
 * Whether an anchor is a cell authored empty, which an insert fills rather
 * than climbs out of; the climb every other grid anchor makes would drop the
 * block after the whole table, nowhere near where the author pointed.
 * A cell that already holds something keeps the climb, so an insert beside its
 * contents still reads as "next block", not "second block in this cell".
 * @param element - The insert's resolved anchor
 * @returns True when the anchor is an empty `<fp-cell>`
 */
function isEmptyCell(element: Element): boolean {
  return (
    element.tagName.toLowerCase() === "fp-cell" &&
    element.children.length === 0 &&
    (element.textContent ?? "").trim() === ""
  );
}

/**
 * Tag names whose children lay out as grid content rather than normal flow: the
 * HTML table boxes, the `<fp-table>` grid, whose children the renderer reads
 * positionally, and the totals band it renders as a table footer.
 *
 * `fp-columns` is absent on purpose: a columns row lays its children out as
 * ordinary flow boxes side by side, so a block belongs INSIDE the column it was
 * aimed at rather than after the whole row. The column stack is what the climb
 * stops at, which is what makes a column hold any block (isGridBox).
 */
const GRID_BOX_TAGS = new Set([
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "fp-table",
  "fp-column",
  "fp-row",
  "fp-cell",
]);

/**
 * Tag names of elements that are themselves grid content, so an insert aimed
 * inside a table lands exactly where it was aimed. `fp-table` is absent on
 * purpose: a whole table dropped among another table's `<fp-column>` children
 * is never read, so it climbs out like any other flow block.
 */
const GRID_CONTENT_TAGS = new Set([
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "fp-column",
  "fp-row",
  "fp-cell",
  "fp-section-heading",
  "fp-section-notes",
]);

/** Class tokens that put an element into a CSS table box. */
const TABLE_BOX_CLASSES = new Set([
  "table",
  "table-caption",
  "table-cell",
  "table-column",
  "table-column-group",
  "table-footer-group",
  "table-header-group",
  "table-row",
  "table-row-group",
]);

/**
 * The element a new block is inserted after so it lands in normal flow: the
 * outermost grid box the anchor sits in.
 *
 * An anchor is often a line repeater, which lives inside a row group, and a
 * flow block placed as its sibling is laid out as anonymous table content - it
 * renders squeezed to one column's width instead of spanning the section. In an
 * `<fp-table>` the same insert is worse than squeezed: the renderer reads only
 * the grid tags it declares, so a stray flow block among them renders nothing
 * at all. The climb stops at the first non-grid parent, so the block lands
 * directly after the table rather than after whatever wraps it. A block that is
 * itself grid content is left where it was aimed.
 * @param anchor - The block the insert is anchored on
 * @param block - The block being inserted
 * @returns The element to insert after
 */
function flowAnchorFor(anchor: Element, block: Element): Element {
  return isGridContent(block) ? anchor : outermostGridBoxOf(anchor);
}

/**
 * Climbs an element out of the grid boxes it sits in, stopping at the first
 * parent that lays its children out in normal flow.
 * @param element - The element to climb from
 * @returns The outermost element still laid out as grid content
 */
function outermostGridBoxOf(element: Element): Element {
  const parent = element.parentElement;
  return parent !== null && isGridBox(parent)
    ? outermostGridBoxOf(parent)
    : element;
}

/**
 * Whether an element is grid content in its own right, by tag or by CSS display
 * class.
 * @param element - The element to test
 * @returns True when the element lays out as part of a table
 */
function isGridContent(element: Element): boolean {
  return (
    GRID_CONTENT_TAGS.has(element.tagName.toLowerCase()) ||
    hasTableDisplayClass(element)
  );
}

/**
 * Whether an element lays its children out as grid content, by tag or by CSS
 * display class. The `<fp-cell>` stack backing an `<fp-columns>` column is the
 * exception: it stacks its children in normal flow, so the climb stops inside it
 * and a block lands in the column rather than after the row.
 * @param element - The element to test
 * @returns True when the element is a grid box
 */
function isGridBox(element: Element): boolean {
  if (isColumnsColumnStack(element)) {
    return false;
  }
  return (
    GRID_BOX_TAGS.has(element.tagName.toLowerCase()) ||
    hasTableDisplayClass(element)
  );
}

/**
 * Whether an element is a column of an `<fp-columns>` row - one of the row's
 * direct children, each of which the renderer resolves into a column of its
 * own, whatever tag it carries.
 * @param element - The element to test
 * @returns True when the element's parent is an `<fp-columns>` row
 */
function isColumnsColumn(element: Element): boolean {
  return element.parentElement?.tagName.toLowerCase() === "fp-columns";
}

/**
 * Whether an element is the `<fp-cell>` stack backing an `<fp-columns>` column,
 * the one column shape that lays its children out in normal flow. Every other
 * column keeps the layout its own tag gives it: a whole `<fp-table>` standing as
 * a column still reads its `<fp-row>` children positionally, so a climb out of
 * that table's cells has to reach the table itself rather than stop at a row -
 * chrome hung off a row would drag the row out of the grid, and an insert
 * anchored on one would drop a flow block among the rows, where the renderer
 * never reads it.
 * @param element - The element to test
 * @returns True when the element is a columns row's cell stack
 */
function isColumnsColumnStack(element: Element): boolean {
  return (
    element.tagName.toLowerCase() === "fp-cell" && isColumnsColumn(element)
  );
}

/**
 * The `<fp-cell>` stack backing a column, promoting a bare column - a single
 * block standing in for the whole column - into one, so the column can hold
 * more than the one block it was authored with.
 * @param doc - The parsed source document
 * @param column - The `<fp-columns>` child to back with a cell
 * @returns The column's cell
 */
function columnCellOf(doc: Document, column: Element): Element {
  if (column.tagName.toLowerCase() === "fp-cell") {
    return column;
  }
  const cell = doc.createElement("fp-cell");
  column.replaceWith(cell);
  cell.append(column);
  return cell;
}

/**
 * Lands a block inside an `<fp-columns>` column's stack rather than beside the
 * column, which would make it a column of its own. The column is promoted to an
 * `<fp-cell>` stack first when it is still a bare block.
 * @param doc - The parsed source document
 * @param column - The `<fp-columns>` child the block was aimed at
 * @param block - The block being placed
 * @param position - Which end of the column's stack the block lands on
 * @returns Nothing; the column is mutated in place
 */
function placeInColumn(
  doc: Document,
  column: Element,
  block: Element,
  position: BlockInsertPosition,
): void {
  const cell = columnCellOf(doc, column);
  const sibling =
    position === "before" ? cell.firstElementChild : cell.lastElementChild;
  if (sibling === null) {
    cell.append(block);
    return;
  }
  if (position === "before") {
    sibling.before(block, "\n");
    return;
  }
  sibling.after("\n", block);
}

/**
 * Drops an `<fp-columns>` column left empty by the block that filled it moving
 * or being deleted, so the row keeps no invisible column - nor its share of the
 * row's width - that nothing can be clicked to put content back into.
 * @param column - The element the block was taken out of, or null
 * @returns Nothing; the row is mutated in place
 */
function pruneEmptiedColumn(column: Element | null): void {
  if (
    column === null ||
    column.tagName.toLowerCase() !== "fp-cell" ||
    !isColumnsColumn(column) ||
    column.children.length > 0 ||
    (column.textContent ?? "").trim() !== ""
  ) {
    return;
  }
  releaseColumnWidth(column);
  column.remove();
}

/**
 * The per-column percentages an `<fp-columns>` row declares, or null when the
 * list does not name every column of a given count in percentages - there is
 * then no total to redistribute, and an unsized column takes an equal flex
 * share by itself.
 * @param row - The `<fp-columns>` row
 * @param count - The column count the list has to cover
 * @returns The percentages in column order, or null
 */
function columnPercents(row: Element, count: number): number[] | null {
  const percents = splitWidths(row.getAttribute("widths")).map(percentValue);
  if (
    percents.length === count &&
    percents.every((width): width is number => width !== null)
  ) {
    return percents;
  }
  return null;
}

/**
 * Rewrites a row's `widths` from one share per surviving column, rescaled to the
 * total the list declared before the column count changed, so the row still
 * fills its space.
 * @param row - The `<fp-columns>` row
 * @param widths - The share per column, in column order
 * @param total - The total the list declared before the change
 * @returns Nothing; the widths attribute is rewritten in place
 */
function writeColumnWidths(
  row: Element,
  widths: number[],
  total: number,
): void {
  const sum = widths.reduce((running, width) => running + width, 0);
  if (sum === 0) {
    row.removeAttribute("widths");
    return;
  }
  row.setAttribute(
    "widths",
    widths.map((width) => `${((width * total) / sum).toFixed(2)}%`).join(","),
  );
}

/**
 * Drops the `widths` entry of a column about to leave its `<fp-columns>` row, so
 * the surviving columns keep filling the row instead of sizing against a share
 * that no longer names a column.
 * @param column - The block about to leave its parent
 * @returns Nothing; the row's widths attribute is rewritten in place
 */
function releaseColumnWidth(column: Element): void {
  const row = column.parentElement;
  if (row === null || !isColumnsColumn(column)) {
    return;
  }
  const percents = columnPercents(row, row.children.length);
  if (percents === null) {
    return;
  }
  const index = Array.from(row.children).indexOf(column);
  writeColumnWidths(
    row,
    percents.filter((_, position) => position !== index),
    percents.reduce((total, width) => total + width, 0),
  );
}

/**
 * Splits a copied column's width between it and the copy standing beside it, so
 * duplicating a column widens the row's column count without widening the row.
 * @param column - The copied column, whose next sibling is the copy
 * @returns Nothing; the row's widths attribute is rewritten in place
 */
function splitDuplicatedColumnWidth(column: Element): void {
  const row = column.parentElement;
  if (row === null || !isColumnsColumn(column)) {
    return;
  }
  const percents = columnPercents(row, row.children.length - 1);
  if (percents === null) {
    return;
  }
  const index = Array.from(row.children).indexOf(column);
  writeColumnWidths(
    row,
    percents.flatMap((width, position) =>
      position === index ? [width / 2, width / 2] : [width],
    ),
    percents.reduce((total, width) => total + width, 0),
  );
}

/**
 * Whether an element's classes put it into a CSS table box.
 * @param element - The element to test
 * @returns True when a class token names a table display mode
 */
function hasTableDisplayClass(element: Element): boolean {
  const classes = element.getAttribute("class")?.split(/\s+/) ?? [];
  return classes.some((token) => TABLE_BOX_CLASSES.has(token));
}

/**
 * Inserts a page break anchored on a nested block by splitting the anchor's
 * top-level ancestor: everything after the anchor (at every nesting level)
 * moves into a structural clone of its ancestors, and the break lands between
 * the two halves. A repeatable header/footer ancestor is never split - the
 * break falls back to a plain insert after it (or before the footer). When
 * nothing follows the anchor the split degenerates to a plain insert after
 * the top-level ancestor. A break landing between the halves of a conditional
 * ancestor inherits its show-when condition, so it disappears with them
 * instead of forcing a page break the condition was meant to suppress. A break
 * inserted BEFORE the anchor splits after the anchor's preceding sibling
 * instead, and lands above the whole ancestor when the anchor opens it.
 * @param doc - The parsed source document
 * @param anchor - The nested block the break was anchored on
 * @param block - The fp-page-break element to insert
 * @param position - Which side of the anchor the break was aimed at
 * @returns The edit with the break selected
 */
function insertPageBreakSplittingAncestors(
  doc: Document,
  anchor: Element,
  block: Element,
  position: BlockInsertPosition,
): DocumentEdit {
  const topLevel = topLevelAncestorOf(doc, anchor);
  if (isRepeatableBlock(topLevel)) {
    if (isRepeatableFooter(topLevel)) {
      insertBeforeFooterOrAppend(doc, [block]);
    } else {
      topLevel.after("\n", block);
    }
    return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
  }
  const showWhen = topLevel.getAttribute(FP_SHOW_WHEN_ATTRIBUTE);
  if (showWhen !== null) {
    block.setAttribute(FP_SHOW_WHEN_ATTRIBUTE, showWhen);
  }
  const splitAfter =
    position === "before" ? anchor.previousElementSibling : anchor;
  if (!splitAfter) {
    topLevel.before(block, "\n");
    return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
  }
  const secondHalf = splitContainerAfter(doc, topLevel, splitAfter);
  topLevel.after("\n", block);
  if (secondHalf) {
    block.after("\n", secondHalf);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
}

/**
 * Builds the second half of a container split after an anchor: the anchor's
 * following siblings at every level move into shallow clones of their
 * ancestors, preserving wrapper tags, classes, and styles. A column-header row
 * (`data-fp-column-header`) staying in the first half is copied into the
 * second so a split line-item table keeps its column widths.
 * @param doc - The parsed source document
 * @param container - The container being split (an ancestor of the anchor)
 * @param anchor - The block the split happens after
 * @returns The second-half element, or null when nothing follows the anchor
 */
function splitContainerAfter(
  doc: Document,
  container: Element,
  anchor: Element,
): Element | null {
  const pathChild = childOnPathTo(container, anchor);
  if (!pathChild) {
    return null;
  }
  const innerTail =
    pathChild === anchor ? null : splitContainerAfter(doc, pathChild, anchor);
  const following: Element[] = [];
  for (
    let sibling = pathChild.nextElementSibling;
    sibling;
    sibling = sibling.nextElementSibling
  ) {
    following.push(sibling);
  }
  if (!innerTail && following.length === 0) {
    return null;
  }
  const clone = container.cloneNode(false);
  if (!(clone instanceof Element)) {
    return null;
  }
  if (innerTail) {
    clone.append(innerTail);
  }
  following.forEach((sibling) => clone.append("\n", sibling));
  const header = Array.from(container.children).find((child) =>
    child.hasAttribute("data-fp-column-header"),
  );
  if (header && header !== anchor && !clone.contains(header)) {
    clone.prepend(header.cloneNode(true), "\n");
  }
  return clone;
}

/**
 * Finds the direct child of a container that contains (or is) the anchor.
 * @param container - The ancestor container
 * @param anchor - The nested block to locate
 * @returns The direct child on the path to the anchor, or null when outside
 */
function childOnPathTo(container: Element, anchor: Element): Element | null {
  return (
    Array.from(container.children).find(
      (child) => child === anchor || child.contains(anchor),
    ) ?? null
  );
}

/**
 * Walks a block up to its top-level ancestor (a direct child of the body).
 * @param doc - The parsed source document
 * @param block - The block to walk up from
 * @returns The top-level ancestor, or the block itself when already top-level
 */
function topLevelAncestorOf(doc: Document, block: Element): Element {
  const parent = block.parentElement;
  return !parent || parent === doc.body
    ? block
    : topLevelAncestorOf(doc, parent);
}

/**
 * Removes a block from the document. Removing a whole `<fp-columns>` column -
 * or the last block inside one - also drops the column's declared width, so the
 * row's surviving columns still fill it. A table cell refuses to delete: a
 * removed cell would leave a hole in its row's grid, so cells only go away with
 * their row or column.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block to remove
 * @returns The edit with selection cleared, or null when the id is invalid
 */
export function removeBlock(html: string, nodeId: string): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block || rendersAsTableCell(block)) {
    return null;
  }
  const origin = block.parentElement;
  releaseColumnWidth(block);
  block.remove();
  pruneEmptiedColumn(origin);
  return { html: serializeDocumentHtml(doc), nodeId: null };
}

/**
 * Appends a bound column to an `<fp-table>`. It lands after the table's last
 * existing column - so the authored rows a table may also carry stay behind the
 * column ruler the renderer reads positionally - wearing that column's look,
 * since every column in these grids declares its own borders, padding and type
 * and a bare one would land outside the table's rules. The new column is
 * selected, so it can be bound and styled straight away.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the `<fp-table>` or one of its columns
 * @param source - The line-scope source the column binds
 * @param header - The column's header text
 * @returns The edit selecting the new column, or null when the id isn't a table
 */
export function appendTableColumn(
  html: string,
  nodeId: string,
  source: string,
  header: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  const table =
    block?.tagName.toLowerCase() === "fp-column"
      ? parentWithTag(block, "fp-table")
      : block;
  if (!table || table.tagName.toLowerCase() !== "fp-table") {
    return null;
  }
  const columns = Array.from(table.children).filter(
    (child) => child.tagName.toLowerCase() === "fp-column",
  );
  const lastColumn = columns[columns.length - 1];
  const declaredTotal = percentageTotal(columns);
  const column = lastColumn
    ? presentationCloneOf(doc, lastColumn)
    : doc.createElement("fp-column");
  column.setAttribute("source", source);
  column.setAttribute("header", header);
  if (lastColumn) {
    lastColumn.after("\n", column);
  } else {
    table.append("\n", column);
  }
  if (declaredTotal !== null) {
    rescaleColumnWidths([...columns, column], declaredTotal);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, column) };
}

/** One reorderable column of the selection's table. */
export interface TableColumnEntry {
  /** Positional node id of the column's anchor: an `<fp-column>` or `<fp-cell>`. */
  nodeId: string;
  /** The column's display label. */
  label: string;
  /** True when the selection sits in this column. */
  selected: boolean;
}

/**
 * Lists the columns of the selection's table in the left-to-right order they
 * render, for the properties panel's reorder list. A bound item table lists
 * its main-row `<fp-column>` declarations - secondary-row columns (`row="N"`)
 * render as full-width bands below the main row, so they take no place in the
 * sideways order. A static table lists its grid columns, each anchored on the
 * first cell starting there, labelled by that cell's text.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the selected block
 * @returns The columns in display order, empty when the selection has none
 */
export function listTableColumns(
  html: string,
  nodeId: string,
): TableColumnEntry[] {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return [];
  }
  const bound = boundTableOf(block);
  if (bound) {
    return mainRowColumns(bound).map((column) => ({
      nodeId: blockIdOf(doc, column),
      label: tableColumnLabel(column),
      selected: column === block,
    }));
  }
  return listStaticTableColumns(doc, block);
}

/**
 * Moves a table's column one step left or right. On a bound item table the
 * anchor is an `<fp-column>` and the move reorders it among the declarations
 * on its row, skipping over declarations of other rows so the swap matches
 * what the user sees. On a static table the anchor is an `<fp-cell>` and the
 * move swaps its grid column with the neighboring one in every row.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the column's anchor
 * @param direction - Which way the column travels
 * @returns The edit with the moved anchor reselected, or null when it can't move
 */
export function moveTableColumn(
  html: string,
  nodeId: string,
  direction: "left" | "right",
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const bound = boundTableOf(block);
  if (bound) {
    return moveBoundTableColumn(doc, block, bound, direction);
  }
  return moveStaticTableColumn(doc, block, direction);
}

/**
 * Reorders a bound table's `<fp-column>` among the declarations on its row.
 * @param doc - The parsed source document
 * @param column - The `<fp-column>` to move
 * @param table - The bound `<fp-table>` holding it
 * @param direction - Which way the column travels
 * @returns The edit with the moved column reselected, or null when it can't move
 */
function moveBoundTableColumn(
  doc: Document,
  column: Element,
  table: Element,
  direction: "left" | "right",
): DocumentEdit | null {
  if (column.parentElement !== table) {
    return null;
  }
  const siblings = mainRowColumns(table);
  const index = siblings.indexOf(column);
  const neighbor = siblings[direction === "left" ? index - 1 : index + 1];
  if (index === -1 || !neighbor) {
    return null;
  }
  if (direction === "left") {
    neighbor.before(column);
  } else {
    neighbor.after(column);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, column) };
}

/**
 * Swaps a static table's grid column with its neighbor, working in grid
 * coordinates from an anchor cell. Every row reaching both columns swaps the
 * two cells; a row holding a `span="all"` cell, or one ending before the left
 * column, is left untouched (a span-all cell stretches over any order, and a
 * short row has nothing there to move). A row whose cells straddle the
 * boundary, or reach only one side of it, refuses the whole move - a partial
 * swap would tear the columns' contents out of alignment. The `<fp-column>`
 * ruler entries swap along, so each column keeps its declared width.
 * @param doc - The parsed source document
 * @param cell - The anchor `<fp-cell>` of the column to move
 * @param direction - Which way the column travels
 * @returns The edit with the anchor reselected, or null when it can't move
 */
function moveStaticTableColumn(
  doc: Document,
  cell: Element,
  direction: "left" | "right",
): DocumentEdit | null {
  if (
    cell.tagName.toLowerCase() !== "fp-cell" ||
    parseTableColumn(cell).span === "all"
  ) {
    return null;
  }
  const row = parentWithTag(cell, "fp-row");
  const table = row ? parentWithTag(row, "fp-table") : null;
  if (!table) {
    return null;
  }
  const start = gridStartOf(cell);
  const target = direction === "left" ? start - 1 : start + 1;
  if (target < 0 || target >= tableGridWidth(table)) {
    return null;
  }
  const left = Math.min(start, target);
  const swaps = directChildren(table, "fp-row")
    .filter(
      (candidate) =>
        !directChildren(candidate, "fp-cell").some(
          (rowCell) => parseTableColumn(rowCell).span === "all",
        ) && (rowLayoutOf(candidate)?.width ?? 0) > left,
    )
    .map((candidate) => {
      const cells = directChildren(candidate, "fp-cell");
      const a = cells.find(
        (rowCell) => gridStartOf(rowCell) === left && gridSpanOf(rowCell) === 1,
      );
      const b = cells.find(
        (rowCell) =>
          gridStartOf(rowCell) === left + 1 && gridSpanOf(rowCell) === 1,
      );
      return a && b ? { a, b } : null;
    });
  const pairs = swaps.filter(
    (pair): pair is { a: Element; b: Element } => pair !== null,
  );
  if (pairs.length !== swaps.length) {
    return null;
  }
  pairs.forEach(({ a, b }) => a.before(b));
  swapRulerColumns(table, left);
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, cell) };
}

/**
 * Swaps two adjacent `<fp-column>` ruler declarations, when the ruler covers
 * both, so a moved static column keeps its declared width.
 * @param table - The static `<fp-table>`
 * @param left - The zero-based index of the left column of the pair
 * @returns Nothing; the ruler is mutated in place
 */
function swapRulerColumns(table: Element, left: number): void {
  const ruler = directChildren(table, "fp-column");
  const a = ruler[left];
  const b = ruler[left + 1];
  if (a && b) {
    a.before(b);
  }
}

/**
 * Lists a static table's grid columns for the reorder list, each anchored on
 * the first cell starting at that column with a plain span. A grid column no
 * cell starts at cleanly has nothing to anchor a move on and stays out.
 * @param doc - The parsed source document
 * @param block - The selected block, in or on the table
 * @returns The columns in display order, empty when the selection has none
 */
function listStaticTableColumns(
  doc: Document,
  block: Element,
): TableColumnEntry[] {
  const context = tableContextOf(block);
  if (!context) {
    return [];
  }
  const rows = directChildren(context.table, "fp-row");
  if (rows.length === 0) {
    return [];
  }
  const selectedColumn =
    context.cell !== null && parseTableColumn(context.cell).span !== "all"
      ? gridStartOf(context.cell)
      : null;
  return Array.from({ length: tableGridWidth(context.table) }, (_, target) => {
    const anchor = staticColumnAnchor(rows, target);
    return anchor === null
      ? null
      : {
          nodeId: blockIdOf(doc, anchor),
          label: staticColumnLabel(anchor, target),
          selected: selectedColumn === target,
        };
  }).filter((entry): entry is TableColumnEntry => entry !== null);
}

/**
 * The first cell across a static table's rows that starts at a grid column
 * with a plain single span, anchoring that column's list entry and moves.
 * @param rows - The table's `<fp-row>` children
 * @param target - The zero-based grid column
 * @returns The anchor cell, or null when no cell starts there cleanly
 */
function staticColumnAnchor(rows: Element[], target: number): Element | null {
  return (
    rows
      .map((row) =>
        directChildren(row, "fp-cell").find(
          (cell) =>
            parseTableColumn(cell).span !== "all" &&
            gridStartOf(cell) === target &&
            gridSpanOf(cell) === 1,
        ),
      )
      .find((cell) => cell !== undefined) ?? null
  );
}

/**
 * A static grid column's display label: its anchor cell's text - usually the
 * header row's - else its one-based position.
 * @param anchor - The column's anchor cell
 * @param target - The zero-based grid column
 * @returns The label
 */
function staticColumnLabel(anchor: Element, target: number): string {
  const text = anchor.textContent?.trim() ?? "";
  return text !== "" ? text : `Column ${target + 1}`;
}

/**
 * The bound `<fp-table>` an element is or belongs to: the nearest enclosing
 * table that repeats quote lines rather than holding authored rows.
 * @param element - The element to resolve, or null
 * @returns The table, or null when the element is not in one
 */
function boundTableOf(element: Element | null): Element | null {
  const table = element?.closest("fp-table") ?? null;
  return table !== null && repeatsQuoteLines(table) ? table : null;
}

/**
 * A bound table's main-row `<fp-column>` declarations, in document order.
 * @param table - The bound `<fp-table>`
 * @returns The columns that take a place in the sideways order
 */
function mainRowColumns(table: Element): Element[] {
  return directChildren(table, "fp-column").filter(
    (column) => parseTableColumn(column).row === 1,
  );
}

/**
 * A column's display label for the reorder list: its header text (stacked
 * `|` parts joined), else its bound field's friendly name.
 * @param column - The `<fp-column>` element
 * @returns The label
 */
function tableColumnLabel(column: Element): string {
  const header = column.getAttribute("header")?.trim() ?? "";
  if (header !== "") {
    return header.split("|").join(" ");
  }
  const source = column.getAttribute("source");
  return source ? lineFieldLabel(source) : "Column";
}

/**
 * `<fp-column>` attributes a presentation clone leaves behind: what the column
 * binds, when it shows, and where it sits. `row` and `span` are placement, not
 * look - copying them from a secondary row, such as a full-width description
 * row, would land the new column on that row instead of the header row.
 */
const COLUMN_UNCOPIED_ATTRIBUTES = [
  "source",
  "header",
  "label",
  "prefix",
  "suffix",
  "repeat",
  "row",
  "span",
  FP_SHOW_WHEN_ATTRIBUTE,
];

/**
 * A fresh column wearing another's look: every attribute it declares except its
 * binding, its visibility gate and its placement, which belong to the column
 * that authored them.
 * @param doc - The parsed source document
 * @param column - The column whose look is copied
 * @returns The new, unbound column on the main row
 */
function presentationCloneOf(doc: Document, column: Element): HTMLElement {
  const clone = doc.createElement("fp-column");
  Array.from(column.attributes)
    .filter((attribute) => !COLUMN_UNCOPIED_ATTRIBUTES.includes(attribute.name))
    .forEach((attribute) =>
      clone.setAttribute(attribute.name, attribute.value),
    );
  return clone;
}

/**
 * The row's total declared width, or null when its columns are not all
 * percentage-sized: such a table sizes itself, so there is no share to divide.
 * @param columns - The table's columns, in render order
 * @returns The summed percentage, or null
 */
function percentageTotal(columns: readonly Element[]): number | null {
  const declared = columns
    .map(percentageWidth)
    .filter((width): width is number => width !== null);
  return declared.length === columns.length && declared.length > 0
    ? declared.reduce((sum, width) => sum + width, 0)
    : null;
}

/**
 * Rescales percentage column widths to a total, which is what keeps a column
 * added to a full-width row inside the table's box. The target is the row's own
 * previous total rather than a flat 100%, since widths are read straight off
 * the declarations and a grid authored to occupy less than the full width keeps
 * the slack it was designed with.
 * @param columns - The table's columns, in render order
 * @param target - The total percentage the widths must sum to
 * @returns Nothing; the width attributes are rewritten in place
 */
function rescaleColumnWidths(
  columns: readonly Element[],
  target: number,
): void {
  const total = columns.reduce(
    (sum, column) => sum + (percentageWidth(column) ?? 0),
    0,
  );
  if (total <= 0) {
    return;
  }
  columns.forEach((column) => {
    const share = ((percentageWidth(column) ?? 0) * target) / total;
    column.setAttribute("width", `${share.toFixed(2)}%`);
  });
}

/**
 * A column's declared width as a percentage.
 * @param column - The `<fp-column>` element
 * @returns The percentage, or null when it declares no percentage width
 */
function percentageWidth(column: Element): number | null {
  return percentValue(column.getAttribute("width") ?? "");
}

/**
 * A declared width string as a percentage.
 * @param width - The declared width (e.g. "30%")
 * @returns The percentage, or null when it is not a percentage
 */
function percentValue(width: string): number | null {
  const value = width.endsWith("%") ? Number.parseFloat(width) : Number.NaN;
  return Number.isFinite(value) ? value : null;
}

/** The narrowest a column may be dragged, as a percentage of the row. */
export const MIN_COLUMN_PERCENT = 2;

/**
 * Resizes a table column by its right edge: the width it gains comes out of the
 * column beside it, so the row's total is untouched and every other column stays
 * exactly where it was. Both columns must be percentage-sized, since an
 * auto-sized pair has no share to trade.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the `<fp-column>` being resized, or of
 * the static `<fp-cell>` whose grid column it is
 * @param percent - The column's new width as a percentage of the row
 * @returns The edit with the resized column named, or null when it cannot resize
 */
export function resizeTableColumn(
  html: string,
  nodeId: string,
  percent: number,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  const columns = block === null ? null : rulerPair(block);
  if (columns === null) {
    return null;
  }
  const [column, neighbour] = columns;
  const columnWidth = percentageWidth(column);
  const neighbourWidth = percentageWidth(neighbour);
  if (columnWidth === null || neighbourWidth === null) {
    return null;
  }
  const pair = columnWidth + neighbourWidth;
  if (pair <= MIN_COLUMN_PERCENT * 2) {
    return null;
  }
  const width = Math.min(
    Math.max(percent, MIN_COLUMN_PERCENT),
    pair - MIN_COLUMN_PERCENT,
  );
  column.setAttribute("width", `${width.toFixed(2)}%`);
  neighbour.setAttribute("width", `${(pair - width).toFixed(2)}%`);
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * The `<fp-column>` pair a resize drag trades width between. A line-bound
 * table's drag names the column itself; a static table's cells carry their own
 * ids instead, so a cell names the ruler columns at its grid position - the
 * ruler being what sizes a static table's grid.
 * @param block - The dragged `<fp-column>`, or a static `<fp-cell>`
 * @returns The column and its neighbour, or null when there is no such pair
 */
function rulerPair(block: Element): [Element, Element] | null {
  const tag = block.tagName.toLowerCase();
  if (tag === "fp-column") {
    const neighbour = nextColumnSibling(block);
    return neighbour === null ? null : [block, neighbour];
  }
  const context = tag === "fp-cell" ? tableContextOf(block) : null;
  if (context === null) {
    return null;
  }
  const ruler = directChildren(context.table, "fp-column");
  const start = gridStartOf(block);
  const column = ruler[start];
  const neighbour = ruler[start + 1];
  return column === undefined || neighbour === undefined
    ? null
    : [column, neighbour];
}

/**
 * The next `<fp-column>` beside one, stepping over the slot elements a table
 * may declare between its columns.
 * @param column - The column to search from
 * @returns The next column, or null when it is the last
 */
function nextColumnSibling(column: Element): Element | null {
  for (
    let sibling = column.nextElementSibling;
    sibling !== null;
    sibling = sibling.nextElementSibling
  ) {
    if (sibling.tagName.toLowerCase() === "fp-column") {
      return sibling;
    }
  }
  return null;
}

/**
 * Resizes an `<fp-columns>` column by its right edge: the width it gains comes
 * out of the column beside it, so the row's total is untouched. The widths live
 * positionally in the row's `widths` attribute, so the column is named by index
 * and both entries must be percentages, since a pair without a declared share
 * has none to trade.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the `<fp-columns>` row
 * @param index - Position of the column being resized
 * @param percent - The column's new width as a percentage of the row
 * @returns The edit with the row named, or null when it cannot resize
 */
export function resizeColumnsColumn(
  html: string,
  nodeId: string,
  index: number,
  percent: number,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const row = resolveBlock(doc, nodeId);
  if (!row || row.tagName.toLowerCase() !== "fp-columns") {
    return null;
  }
  const widths = splitWidths(row.getAttribute("widths"));
  const width = percentValue(widths[index] ?? "");
  const neighbourWidth = percentValue(widths[index + 1] ?? "");
  if (width === null || neighbourWidth === null) {
    return null;
  }
  const pair = width + neighbourWidth;
  if (pair <= MIN_COLUMN_PERCENT * 2) {
    return null;
  }
  const next = Math.min(
    Math.max(percent, MIN_COLUMN_PERCENT),
    pair - MIN_COLUMN_PERCENT,
  );
  widths[index] = `${next.toFixed(2)}%`;
  widths[index + 1] = `${(pair - next).toFixed(2)}%`;
  row.setAttribute("widths", widths.join(","));
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Appends a new column to the anchor block's enclosing `<fp-columns>` row. The
 * column is an `<fp-cell>` stack, so any block can be inserted or dropped into
 * it, seeded with placeholder text because an empty column renders no box to
 * click. A fully percentage-sized `widths` list rescales so the new column gets
 * an equal share; any other list is left alone, since an unsized column takes
 * an equal flex share by itself.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the row or a block inside it
 * @returns The edit with the new column selected, or null when the anchor is
 * not in an `<fp-columns>` row
 */
export function addColumnsColumn(
  html: string,
  nodeId: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  const row = block === null ? null : enclosingColumnsRow(block);
  if (row === null) {
    return null;
  }
  const count = row.children.length;
  const column = doc.createElement("fp-cell");
  const placeholder = doc.createElement("fp-text");
  placeholder.textContent = "New column";
  column.append(placeholder);
  row.append("\n", column);
  const percents = columnPercents(row, count);
  if (percents !== null) {
    const total = percents.reduce((sum, width) => sum + width, 0);
    const share = total / (count + 1);
    const scaled = percents.map((width) => (width * (total - share)) / total);
    row.setAttribute(
      "widths",
      [...scaled, share].map((width) => `${width.toFixed(2)}%`).join(","),
    );
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, column) };
}

/**
 * Splits an `<fp-columns>` `widths` attribute into its per-column entries.
 * @param widths - The raw comma-separated attribute, or null when absent
 * @returns The per-column width entries, empty when absent
 */
function splitWidths(widths: string | null): string[] {
  if (widths === null) {
    return [];
  }
  return widths
    .split(",")
    .map((width) => width.trim())
    .filter((width) => width !== "");
}

/**
 * The `<fp-columns>` row enclosing a block, the block itself included.
 * @param block - The block to climb from
 * @returns The enclosing row, or null when the block is not in one
 */
function enclosingColumnsRow(block: Element): Element | null {
  for (
    let current: Element | null = block;
    current !== null;
    current = current.parentElement
  ) {
    if (current.tagName.toLowerCase() === "fp-columns") {
      return current;
    }
  }
  return null;
}

/**
 * Duplicates a block, inserting the copy immediately after the original.
 * Duplicating a whole `<fp-columns>` column splits its declared width with the
 * copy, so the row gains a column without outgrowing its space.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block to duplicate
 * @returns The edit with the copy selected, or null when the id is invalid
 */
export function duplicateBlock(
  html: string,
  nodeId: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const clone = block.cloneNode(true);
  if (!(clone instanceof HTMLElement)) {
    return null;
  }
  block.after("\n", clone);
  splitDuplicatedColumnWidth(block);
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, clone) };
}

/**
 * Moves a block one step up or down among the top-level blocks. The
 * repeatable header and footer neither move nor are valid swap partners (they
 * are lifted out of the flow at render time), so a move of one or across one
 * is a no-op.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block to move
 * @param direction - "up" swaps with the previous block, "down" with the next
 * @returns The edit with the moved block reselected, or null when it can't move
 */
export function moveBlockStep(
  html: string,
  nodeId: string,
  direction: "up" | "down",
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block || isRepeatableBlock(block)) {
    return null;
  }
  const neighbor =
    direction === "up"
      ? block.previousElementSibling
      : block.nextElementSibling;
  if (!neighbor || isRepeatableBlock(neighbor)) {
    return null;
  }
  if (direction === "up") {
    neighbor.before(block);
  } else {
    neighbor.after(block);
  }
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
}

/**
 * Moves a block before or after an anchor block (drag-reorder drop). An anchor
 * inside a table climbs out to the whole table, the same climb an insert makes:
 * a flow block landing beside a cell renders squeezed or, in an `<fp-table>`,
 * not at all, so the drop would silently swallow it. A drop on a whole
 * `<fp-columns>` column lands inside that column instead, so a block can be
 * dragged into a column the way it can be inserted into one. A column dragged
 * out of its row - or emptied by its last block being dragged out - releases its
 * declared width, so the row still fills its space.
 * The repeatable header and footer are neither valid subjects nor valid
 * anchors: they are lifted out of the flow at render time, so a drop involving
 * one is a no-op.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block being moved
 * @param anchorNodeId - Positional node id of the drop anchor block
 * @param position - Whether the block lands before or after the anchor
 * @returns The edit with the moved block reselected, or null when invalid
 */
export function moveBlockRelative(
  html: string,
  nodeId: string,
  anchorNodeId: string,
  position: "before" | "after",
): DocumentEdit | null {
  if (nodeId === anchorNodeId) {
    return null;
  }
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  const hit = resolveBlock(doc, anchorNodeId);
  if (!block || !hit) {
    return null;
  }
  const anchor = flowAnchorFor(hit, block);
  if (anchor === block || block.contains(anchor)) {
    return null;
  }
  if (isRepeatableBlock(block) || isRepeatableBlock(anchor)) {
    return null;
  }
  const origin = block.parentElement;
  releaseColumnWidth(block);
  if (isColumnsColumn(anchor)) {
    placeInColumn(doc, anchor, block, position);
  } else if (position === "before") {
    anchor.before(block);
  } else {
    anchor.after(block);
  }
  pruneEmptiedColumn(origin);
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, block) };
}

/**
 * Writes one inline-style property on a block's source element. An empty
 * value removes the property (and the style attribute when it empties out).
 * Writing a type-style property (`fontSize`, `fontWeight`, `fontStyle`,
 * `textDecoration`) also drops the block's matching container attribute
 * (`size`, `weight`, `font-style`, `decoration`), the other channel authored
 * documents set type through, so the style the knob just wrote is the only
 * one left describing it.
 *
 * An `<fp-table>` column is the exception: its cells render from its container
 * attributes, never from its own inline style, so the knob writes the mapped
 * attribute instead and an empty value removes it.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block
 * @param property - The camelCased style property to write
 * @param value - The CSS value, or "" to unset
 * @returns The edit with the block reselected, or null when the id is invalid
 */
export function setBlockStyle(
  html: string,
  nodeId: string,
  property: StyleProperty,
  value: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  if (block.tagName.toLowerCase() === COLUMN_TAG) {
    const attribute = COLUMN_STYLE_ATTRIBUTES[property];
    if (value.trim() === "") {
      block.removeAttribute(attribute);
    } else {
      block.setAttribute(attribute, value);
    }
    return { html: serializeDocumentHtml(doc), nodeId };
  }
  const typeAttribute = TYPE_STYLE_ATTRIBUTES[property];
  if (typeAttribute !== undefined) {
    block.removeAttribute(typeAttribute);
  }
  if (value.trim() === "") {
    block.style.removeProperty(toKebabCase(property));
  } else {
    block.style.setProperty(toKebabCase(property), value);
  }
  if (!block.getAttribute("style")) {
    block.removeAttribute("style");
  }
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Adds or removes one class on a block's source element, leaving every other
 * class and the inline `style` attribute untouched. The `class` attribute is
 * dropped when the last class goes.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block
 * @param className - The single class to toggle
 * @param enabled - True to add the class, false to remove it
 * @returns The edit with the block reselected, or null when the id is invalid
 */
export function setBlockClass(
  html: string,
  nodeId: string,
  className: string,
  enabled: boolean,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  block.classList.toggle(className, enabled);
  if (block.classList.length === 0) {
    block.removeAttribute("class");
  }
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Writes (or removes) an attribute on a block's source element — used for
 * `source` on fp-field/fp-markdown and `asset-id` on fp-image.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block
 * @param attribute - The attribute name
 * @param value - The attribute value, or "" to remove it
 * @returns The edit with the block reselected, or null when the id is invalid
 */
export function setBlockAttribute(
  html: string,
  nodeId: string,
  attribute: string,
  value: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const fansOutToCells =
    block.tagName.toLowerCase() === "fp-row" && attribute === "background";
  const targets = fansOutToCells ? directChildren(block, "fp-cell") : [block];
  for (const target of targets) {
    if (value.trim() === "") {
      target.removeAttribute(attribute);
    } else {
      target.setAttribute(attribute, value);
    }
  }
  if (fansOutToCells) {
    block.removeAttribute(attribute);
  }
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Replaces the inner HTML of a repeater block's direct `<template>` child with
 * new markup, committed as one parse-safe mutation. The markup comes from the
 * column editor, which only ever moves authored cells around; the server-side
 * sanitizer remains the authoritative gate on save.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the repeater block
 * @param innerHtml - The new template inner HTML
 * @returns The edit with the repeater reselected, or null when invalid
 */
export function replaceTemplateInner(
  html: string,
  nodeId: string,
  innerHtml: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const template = directTemplateOf(block);
  if (!template) {
    return null;
  }
  template.innerHTML = innerHtml;
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Inserts an empty row above or below the anchor row of a static table. The
 * anchor is the selected `<fp-row>`, or the selected `<fp-cell>`'s row. The new
 * row gets one empty cell per grid column of the table (its widest row measured
 * by spans, or its `<fp-column>` ruler when that is wider), so it lines up with
 * the established grid even when the anchor row is narrower or spans cells.
 * Each new cell wears the look of the anchor row's cell in the same grid column
 * - its width, padding, border and type - since these grids carry their rules on
 * the cells themselves and a bare row would land outside them. A merged cell
 * spanning across the insertion point grows its rowspan instead, and the new
 * row skips the columns it covers.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the anchor `<fp-row>` or `<fp-cell>`
 * @param position - Whether the new row lands above or below the anchor row
 * @returns The edit with the new row's cell at the anchor cell's grid column
 * selected (its first cell when the anchor was the row itself, the row when it
 * has no cells), or null when the anchor is not inside a static table row
 */
export function insertTableRow(
  html: string,
  nodeId: string,
  position: "above" | "below",
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.row === null) {
    return null;
  }
  const anchorRow = context.row;
  const layouts = tableRowLayouts(context.table);
  const anchorIndex = layouts.findIndex((layout) => layout.row === anchorRow);
  const boundary = position === "above" ? anchorIndex : anchorIndex + 1;
  const crossing = layouts[boundary]?.carryover ?? [];
  const covered = new Set(
    crossing.flatMap((slot) =>
      Array.from(
        { length: slot.span },
        (_unused, offset) => slot.start + offset,
      ),
    ),
  );
  const freeColumns = Array.from(
    { length: tableGridWidth(context.table) },
    (_unused, column) => column,
  ).filter((column) => !covered.has(column));
  crossing.forEach((slot) => setRowspan(slot.cell, rowspanOf(slot.cell) + 1));
  const row = doc.createElement("fp-row");
  row.append(
    ...freeColumns.map((column) =>
      emptyTableCell(doc, cellAtColumn(anchorRow, column)),
    ),
  );
  if (position === "above") {
    anchorRow.before(row, "\n");
  } else {
    anchorRow.after("\n", row);
  }
  const target = context.cell === null ? 0 : gridStartOf(context.cell);
  const cells = directChildren(row, "fp-cell");
  const selected = cells[freeColumns.indexOf(target)] ?? cells[0] ?? row;
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, selected) };
}

/**
 * Removes the anchor row of a static table: the selected `<fp-row>`, or the
 * selected `<fp-cell>`'s row. The table's last row refuses to delete, so a
 * table can never be emptied out. A merged cell spanning through the row
 * shrinks its rowspan by one, and one starting in the row moves down into the
 * next row so the rest of its merge survives.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the anchor `<fp-row>` or `<fp-cell>`
 * @returns The edit with the table reselected, or null when the anchor is not
 * a removable static table row
 */
export function removeTableRow(
  html: string,
  nodeId: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.row === null) {
    return null;
  }
  const layouts = tableRowLayouts(context.table);
  if (layouts.length <= 1) {
    return null;
  }
  const index = layouts.findIndex((layout) => layout.row === context.row);
  const layout = layouts[index];
  layout.carryover.forEach((slot) =>
    setRowspan(slot.cell, rowspanOf(slot.cell) - 1),
  );
  const next = layouts[index + 1];
  if (next) {
    layout.slots
      .filter((slot) => slot.rowspan > 1)
      .forEach((slot) => {
        setRowspan(slot.cell, slot.rowspan - 1);
        const following = next.slots.find((entry) => entry.start > slot.start);
        if (following) {
          following.cell.before(slot.cell);
        } else {
          next.row.append(slot.cell);
        }
      });
  }
  context.row.remove();
  return {
    html: serializeDocumentHtml(doc),
    nodeId: blockIdOf(doc, context.table),
  };
}

/**
 * Inserts an empty grid column beside the anchor cell of a static table,
 * working in grid coordinates so spanned cells stay aligned. In each row a new
 * empty cell lands at the target column; a cell spanning across the target
 * grows its span by one instead of being split; a row holding a `span="all"`
 * cell, or one too narrow to reach the target, is left untouched (a span-all
 * cell stretches to the new width by itself, and a short row keeps its cells in
 * their columns). A `<fp-column>` ruler, when present, gains a matching
 * declaration so the widths of the following columns do not shift.
 *
 * A new column takes an equal share of whatever width the grid already declares
 * - on the ruler, and on each row whose cells are all percentage-sized - taken
 * proportionally from the columns beside it. Without that share a grid that
 * already claimed the full width would size the new column to nothing, so the
 * column the author just asked for would not appear at all.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the anchor `<fp-cell>`
 * @param side - Which side of the anchor cell the new column lands on
 * @returns The edit with the anchor row's new cell selected, or null when the
 * anchor is not a spannable cell of a static table
 */
export function insertTableColumn(
  html: string,
  nodeId: string,
  side: "left" | "right",
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.cell === null || context.row === null) {
    return null;
  }
  if (parseTableColumn(context.cell).span === "all") {
    return null;
  }
  const start = gridStartOf(context.cell);
  const target = side === "left" ? start : start + gridSpanOf(context.cell);
  const entries = tableRowLayouts(context.table).map((layout) => ({
    row: layout.row,
    cell: insertCellAtColumn(doc, layout, target),
  }));
  entries.forEach((entry) => {
    if (entry.cell !== null) {
      shareDeclaredWidth(entry.row, "fp-cell", entry.cell);
    }
  });
  insertRulerColumn(doc, context.table, target);
  const inserted = entries.find((entry) => entry.row === context.row)?.cell;
  return {
    html: serializeDocumentHtml(doc),
    nodeId: blockIdOf(doc, inserted ?? context.row),
  };
}

/**
 * Removes the grid column the anchor cell starts at from a static table,
 * working in grid coordinates. In each row the cell occupying that column is
 * removed; a cell spanning across it shrinks by one instead; a row holding a
 * `span="all"` cell, or one too narrow to reach the column, is left untouched.
 * A row emptied of its last cell goes with the column, as does the matching
 * `<fp-column>` ruler declaration when the table has one. The table's last
 * column refuses to delete, so a table can never be emptied out.
 *
 * The removed column's declared width goes back to the columns left, on the
 * ruler and on each row that was fully percentage-sized, so a grid that filled
 * its box still does instead of shrinking away from the table's right edge.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the anchor `<fp-cell>`
 * @returns The edit reselecting the anchor cell when it survives (it spanned
 * more than one column), else its row, else the table; null when the anchor is
 * not a removable column's cell
 */
export function removeTableColumn(
  html: string,
  nodeId: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.cell === null || context.row === null) {
    return null;
  }
  if (parseTableColumn(context.cell).span === "all") {
    return null;
  }
  if (tableGridWidth(context.table) <= 1) {
    return null;
  }
  const target = gridStartOf(context.cell);
  tableRowLayouts(context.table).forEach((layout) => {
    const cells = layout.slots.map((slot) => slot.cell);
    const total = percentageTotal(cells);
    removeCellAtColumn(layout, target);
    const remaining = directChildren(layout.row, "fp-cell");
    if (remaining.length === 0) {
      collapseEmptiedRow(layout.row);
      return;
    }
    if (total !== null && remaining.length < cells.length) {
      rescaleColumnWidths(remaining, total);
    }
  });
  const ruler = directChildren(context.table, "fp-column");
  const rulerTotal = percentageTotal(ruler);
  ruler[target]?.remove();
  if (rulerTotal !== null && target < ruler.length) {
    rescaleColumnWidths(directChildren(context.table, "fp-column"), rulerTotal);
  }
  const survivor = [context.cell, context.row, context.table].find(
    (element) => element.isConnected,
  );
  return {
    html: serializeDocumentHtml(doc),
    nodeId: blockIdOf(doc, survivor ?? context.table),
  };
}

/**
 * Merges the anchor cell of a static table with its neighbours in one
 * direction. A one-to-one merge with an equal-sized neighbour keeps the cell
 * nearer the grid's top-left, taking the absorbed cell's text after its own.
 * When the neighbouring side instead holds several smaller cells exactly
 * tiling the anchor's edge - a row of narrower cells above or below, a stack
 * of shorter cells beside it - the whole run is absorbed: the anchor survives,
 * its content overrides theirs, and merging up moves it into their place. A
 * row emptied by a merge collapses, shrinking the rowspans that covered it.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the anchor `<fp-cell>`
 * @param direction - Which neighbour the anchor cell merges with
 * @returns The edit with the merged cell selected, or null when no aligned
 * neighbour exists in that direction
 */
export function mergeTableCells(
  html: string,
  nodeId: string,
  direction: MergeDirection,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.cell === null) {
    return null;
  }
  const resolution = resolveMerge(context.table, context.cell, direction);
  if (resolution === null) {
    return null;
  }
  if (resolution.kind === "pair") {
    const { partner } = resolution;
    const survivor =
      direction === "left" || direction === "up" ? partner : context.cell;
    const absorbed = survivor === partner ? context.cell : partner;
    absorbCellContent(survivor, absorbed);
    if (direction === "left" || direction === "right") {
      setSpan(survivor, gridSpanOf(survivor) + gridSpanOf(absorbed));
    } else {
      setRowspan(survivor, rowspanOf(survivor) + rowspanOf(absorbed));
    }
    const origin = absorbed.parentElement;
    absorbed.remove();
    if (origin !== null) {
      collapseEmptiedRow(origin);
    }
    return {
      html: serializeDocumentHtml(doc),
      nodeId: blockIdOf(doc, survivor),
    };
  }
  const { group } = resolution;
  const anchor = context.cell;
  const origin = anchor.parentElement;
  const touched = [...new Set(group.cells.map((cell) => cell.parentElement))];
  group.landing?.before(anchor);
  group.cells.forEach((absorbed) => absorbed.remove());
  if (group.extraRows > 0) {
    setRowspan(anchor, rowspanOf(anchor) + group.extraRows);
  }
  if (group.extraColumns > 0) {
    setSpan(anchor, gridSpanOf(anchor) + group.extraColumns);
  }
  [...touched, origin]
    .filter((row): row is HTMLElement => row !== null)
    .filter((row) => row !== anchor.parentElement)
    .forEach((row) => collapseEmptiedRow(row));
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, anchor) };
}

/** A one-to-one merge with an equal-sized neighbouring cell. */
interface PairMerge {
  kind: "pair";
  partner: Element;
}

/** A merge absorbing a run of smaller cells tiling one edge of the anchor. */
interface GroupMerge {
  kind: "group";
  group: MergeGroup;
}

/** The run of smaller cells a group merge absorbs. */
interface MergeGroup {
  /** The absorbed cells, in grid order; the anchor's content overrides theirs. */
  cells: Element[];
  /** Rows a vertical merge adds to the anchor's rowspan; zero horizontally. */
  extraRows: number;
  /** Columns a horizontal merge adds to the anchor's span; zero vertically. */
  extraColumns: number;
  /** The leftmost absorbed cell, which the anchor moves in front of on an
   * upward merge so the merged cell starts where the run did; null in every
   * other direction, where the anchor's own position already leads the run. */
  landing: Element | null;
}

/**
 * Resolves what a merge in one direction absorbs: the single equal-sized
 * neighbour when one lines up, else the run of smaller cells against the
 * anchor's edge that exactly tiles it - a row of narrower cells above or
 * below, a stack of shorter cells beside it. A `span="all"` cell on either
 * side refuses - it occupies no columns of its own to merge.
 * @param table - The `<fp-table>` element
 * @param cell - The anchor `<fp-cell>`
 * @param direction - Which neighbour to look for
 * @returns The resolution, or null when nothing lines up
 */
function resolveMerge(
  table: Element,
  cell: Element,
  direction: MergeDirection,
): PairMerge | GroupMerge | null {
  if (parseTableColumn(cell).span === "all") {
    return null;
  }
  const layouts = tableRowLayouts(table);
  const location = layouts
    .flatMap((layout, rowIndex) =>
      layout.slots
        .filter((slot) => slot.cell === cell)
        .map((slot) => ({ slot, rowIndex })),
    )
    .find(() => true);
  if (!location) {
    return null;
  }
  const { slot, rowIndex } = location;
  const partner = findMergePartner(layouts, slot, rowIndex, direction);
  if (partner !== null) {
    return parseTableColumn(partner).span !== "all"
      ? { kind: "pair", partner }
      : null;
  }
  const group =
    direction === "left" || direction === "right"
      ? horizontalMergeGroupOf(layouts, slot, rowIndex, direction)
      : verticalMergeGroupOf(layouts, slot, rowIndex, direction);
  return group === null ? null : { kind: "group", group };
}

/**
 * Locates the single aligned neighbour slot for a merge, by direction.
 * @param layouts - The table's row layouts
 * @param slot - The anchor cell's slot
 * @param rowIndex - The anchor cell's row index
 * @param direction - Which neighbour to look for
 * @returns The partner cell, or null when none lines up
 */
function findMergePartner(
  layouts: RowLayout[],
  slot: CellSlot,
  rowIndex: number,
  direction: MergeDirection,
): Element | null {
  if (direction === "left" || direction === "right") {
    const partner = layouts[rowIndex].slots.find((candidate) =>
      direction === "left"
        ? candidate.start + candidate.span === slot.start
        : candidate.start === slot.start + slot.span,
    );
    return partner && partner.rowspan === slot.rowspan ? partner.cell : null;
  }
  if (direction === "down") {
    const below = layouts[rowIndex + slot.rowspan];
    const partner = below?.slots.find(
      (candidate) =>
        candidate.start === slot.start && candidate.span === slot.span,
    );
    return partner?.cell ?? null;
  }
  const partner = layouts
    .slice(0, rowIndex)
    .flatMap((layout, aboveIndex) =>
      layout.slots.filter(
        (candidate) =>
          candidate.start === slot.start &&
          candidate.span === slot.span &&
          aboveIndex + candidate.rowspan === rowIndex,
      ),
    )
    .find(() => true);
  return partner?.cell ?? null;
}

/**
 * The run of smaller cells in the adjacent row that a vertical merge absorbs:
 * every cell directly against the anchor's edge, all sharing one row band and
 * together tiling the anchor's columns exactly - a run sticking out past the
 * anchor, or leaving a gap, would make the merged region non-rectangular.
 * @param layouts - The table's row layouts
 * @param slot - The anchor cell's slot
 * @param rowIndex - The anchor cell's row index
 * @param direction - Whether the run sits above or below the anchor
 * @returns The group, or null when the adjacent cells do not tile the anchor
 */
function verticalMergeGroupOf(
  layouts: RowLayout[],
  slot: CellSlot,
  rowIndex: number,
  direction: "up" | "down",
): MergeGroup | null {
  const end = slot.start + slot.span;
  if (direction === "down") {
    const below = layouts[rowIndex + slot.rowspan];
    const group = (below?.slots ?? []).filter(
      (candidate) =>
        candidate.start < end && candidate.start + candidate.span > slot.start,
    );
    const depth = group[0]?.rowspan ?? 0;
    if (
      !tilesColumns(group, slot.start, end) ||
      !group.every((candidate) => candidate.rowspan === depth) ||
      group.some((candidate) => parseTableColumn(candidate.cell).span === "all")
    ) {
      return null;
    }
    return {
      cells: group.map((candidate) => candidate.cell),
      extraRows: depth,
      extraColumns: 0,
      landing: null,
    };
  }
  const above = layouts
    .slice(0, rowIndex)
    .flatMap((layout, top) =>
      layout.slots
        .filter(
          (candidate) =>
            top + candidate.rowspan === rowIndex &&
            candidate.start < end &&
            candidate.start + candidate.span > slot.start,
        )
        .map((candidate) => ({ candidate, top })),
    );
  const group = above.map((entry) => entry.candidate);
  if (
    above.length === 0 ||
    !above.every((entry) => entry.top === above[0].top) ||
    !tilesColumns(group, slot.start, end) ||
    group.some((candidate) => parseTableColumn(candidate.cell).span === "all")
  ) {
    return null;
  }
  return {
    cells: group.map((candidate) => candidate.cell),
    extraRows: rowIndex - above[0].top,
    extraColumns: 0,
    landing: group[0].cell,
  };
}

/**
 * The stack of smaller cells beside the anchor that a horizontal merge
 * absorbs: every cell directly against the anchor's edge, all one column band
 * wide, together tiling the anchor's rows exactly - a stack sticking out past
 * the anchor, or leaving a gap, would make the merged region non-rectangular.
 * The anchor keeps its own position: absorbing the stack on its left frees
 * those columns, which the anchor slides into by document order.
 * @param layouts - The table's row layouts
 * @param slot - The anchor cell's slot
 * @param rowIndex - The anchor cell's row index
 * @param direction - Whether the stack sits left or right of the anchor
 * @returns The group, or null when the adjacent cells do not tile the anchor
 */
function horizontalMergeGroupOf(
  layouts: RowLayout[],
  slot: CellSlot,
  rowIndex: number,
  direction: "left" | "right",
): MergeGroup | null {
  const rowEnd = rowIndex + slot.rowspan;
  const stack = layouts
    .slice(rowIndex, rowEnd)
    .flatMap((layout, offset) =>
      layout.slots
        .filter((candidate) =>
          direction === "right"
            ? candidate.start === slot.start + slot.span
            : candidate.start + candidate.span === slot.start,
        )
        .map((candidate) => ({ candidate, top: rowIndex + offset })),
    );
  const group = stack.map((entry) => entry.candidate);
  const width = group[0]?.span ?? 0;
  const covered = stack.reduce(
    (position, entry) =>
      entry.top === position ? position + entry.candidate.rowspan : -1,
    rowIndex,
  );
  if (
    group.length === 0 ||
    covered !== rowEnd ||
    !group.every((candidate) => candidate.span === width) ||
    group.some((candidate) => parseTableColumn(candidate.cell).span === "all")
  ) {
    return null;
  }
  return {
    cells: group.map((candidate) => candidate.cell),
    extraRows: 0,
    extraColumns: width,
    landing: null,
  };
}

/**
 * Whether a run of slots covers exactly the columns from `start` to `end`:
 * contiguous, starting and ending on the anchor's own edges.
 * @param group - The candidate slots, in grid order
 * @param start - The anchor's first grid column
 * @param end - The grid column past the anchor's last
 * @returns True when the run tiles the range exactly
 */
function tilesColumns(group: CellSlot[], start: number, end: number): boolean {
  const covered = group.reduce(
    (position, candidate) =>
      candidate.start === position ? position + candidate.span : -1,
    start,
  );
  return group.length > 0 && covered === end;
}

/**
 * Moves an absorbed cell's content to the end of the surviving cell's, with a
 * blank line between the two bodies when both hold something - a cell owns its
 * text as markdown, so the separator keeps the merged bodies as distinct
 * paragraphs rather than gluing them into one word.
 * @param survivor - The cell that keeps the merged content
 * @param absorbed - The cell whose content moves
 */
function absorbCellContent(survivor: Element, absorbed: Element): void {
  const bothFilled =
    (survivor.textContent ?? "").trim() !== "" &&
    (absorbed.textContent ?? "").trim() !== "";
  if (bothFilled) {
    survivor.append("\n\n");
  }
  survivor.append(...Array.from(absorbed.childNodes));
}

/**
 * Splits a merged cell of a static table back into single cells. The anchor
 * keeps its own content and drops its `span` and `rowspan`; every other grid
 * position the merge covered gains an empty cell wearing the anchor's look, so
 * the rows come back to the width the grid expects. A covered row the table
 * does not have - a rowspan overhanging the last row - is skipped.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the merged `<fp-cell>`
 * @returns The edit with the anchor cell still selected, or null when the
 * anchor is not a merged cell
 */
export function unmergeTableCells(
  html: string,
  nodeId: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const context = tableContextFor(doc, nodeId);
  if (!context || context.cell === null || !isUnmergeable(context.cell)) {
    return null;
  }
  const anchor = context.cell;
  const layouts = tableRowLayouts(context.table);
  const rowIndex = layouts.findIndex((layout) =>
    layout.slots.some((slot) => slot.cell === anchor),
  );
  const slot = layouts[rowIndex]?.slots.find((entry) => entry.cell === anchor);
  if (!slot) {
    return null;
  }
  setSpan(anchor, 1);
  setRowspan(anchor, 1);
  anchor.after(
    ...Array.from({ length: slot.span - 1 }, () => emptyTableCell(doc, anchor)),
  );
  layouts
    .slice(rowIndex + 1, rowIndex + slot.rowspan)
    .forEach((layout) => fillUnmergedRow(doc, layout, anchor, slot));
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, anchor) };
}

/**
 * Fills the grid columns of one row that a split merge no longer covers with
 * empty cells wearing the anchor's look, landing them at the anchor's grid
 * column - before the row's first cell starting past it, else at the row's end.
 * @param doc - The parsed source document
 * @param layout - The covered row's layout, captured before any row was mutated
 * @param anchor - The cell being split, whose look the new cells copy
 * @param slot - The anchor's grid placement, captured before it was split
 */
function fillUnmergedRow(
  doc: Document,
  layout: RowLayout,
  anchor: Element,
  slot: CellSlot,
): void {
  const fillers = Array.from({ length: slot.span }, () =>
    emptyTableCell(doc, anchor),
  );
  const following = layout.slots.find((entry) => entry.start > slot.start);
  if (following) {
    following.cell.before(...fillers);
    return;
  }
  layout.row.append(...fillers);
}

/**
 * Whether a cell is a merge that can be split apart: it covers more than one
 * grid position, and is not a `span="all"` banner - that one stretches to the
 * table's width rather than covering columns of its own, so it has nothing to
 * give back.
 * @param cell - The `<fp-cell>` element
 * @returns True when the cell can be unmerged
 */
function isUnmergeable(cell: Element): boolean {
  if (parseTableColumn(cell).span === "all") {
    return false;
  }
  return gridSpanOf(cell) > 1 || rowspanOf(cell) > 1;
}

/**
 * Builds the static-table inspector info for a selected table, row, or cell.
 * A table without `<fp-row>` children binds its rows to quote lines instead of
 * authored content, so it reports null and keeps the table inspector away.
 * @param block - The selected source element
 * @returns The table info, or null for anything but a static table selection
 */
function summarizeTable(block: Element): TableInfo | null {
  const context = tableContextOf(block);
  if (!context) {
    return null;
  }
  const rows = directChildren(context.table, "fp-row");
  if (rows.length === 0) {
    return null;
  }
  const canEditColumns =
    context.cell !== null && parseTableColumn(context.cell).span !== "all";
  const canMerge = (direction: MergeDirection) =>
    context.cell !== null &&
    resolveMerge(context.table, context.cell, direction) !== null;
  return {
    canEditRows: context.row !== null,
    canRemoveRow: context.row !== null && rows.length > 1,
    canEditColumns,
    canRemoveColumn: canEditColumns && tableGridWidth(context.table) > 1,
    canMergeLeft: canMerge("left"),
    canMergeRight: canMerge("right"),
    canMergeUp: canMerge("up"),
    canMergeDown: canMerge("down"),
    canUnmerge: context.cell !== null && isUnmergeable(context.cell),
  };
}

/** The selected tags whose enclosing table the column grips hang off. */
const TABLE_PART_TAGS: ReadonlySet<string> = new Set([
  "fp-table",
  COLUMN_TAG,
  "fp-row",
  "fp-cell",
]);

/**
 * The node id of the `<fp-table>` a selected table part belongs to. An
 * `<fp-columns>` column is an `<fp-cell>` with no table around it, so it
 * resolves to null and draws no table grips.
 * @param doc - The parsed source document
 * @param block - The selected source element
 * @returns The table's node id, or null when the selection is not a table part
 */
function enclosingTableNodeId(doc: Document, block: Element): string | null {
  if (!TABLE_PART_TAGS.has(block.tagName.toLowerCase())) {
    return null;
  }
  const context = tableContextOf(block);
  return context === null ? null : blockIdOf(doc, context.table);
}

/**
 * Builds the columns-row inspector info for a selection: the enclosing
 * `<fp-columns>` row when the selected block is one or sits inside one.
 * @param doc - The parsed source document
 * @param block - The selected source element
 * @returns The columns info, or null when no row encloses the selection
 */
function summarizeColumns(doc: Document, block: Element): ColumnsInfo | null {
  const row = enclosingColumnsRow(block);
  return row === null ? null : { nodeId: blockIdOf(doc, row) };
}

/** The static-table elements a selection anchors. */
interface TableContext {
  /** The enclosing `<fp-table>`. */
  table: Element;
  /** The anchor row: the selected row, or the selected cell's row. */
  row: Element | null;
  /** The anchor cell, when the selection is a cell. */
  cell: Element | null;
}

/**
 * Resolves a node id to its static-table context.
 * @param doc - The parsed source document
 * @param nodeId - Positional node id of a table, row, or cell
 * @returns The table context, or null when the id resolves to anything else
 */
function tableContextFor(doc: Document, nodeId: string): TableContext | null {
  const block = resolveBlock(doc, nodeId);
  return block ? tableContextOf(block) : null;
}

/**
 * The table context of a selected element: the enclosing `<fp-table>` plus the
 * anchor row and cell the selection provides. Content selected inside a cell
 * (a text block, a field) anchors on that cell, so drilling into a cell's text
 * keeps the row and column operations available instead of dropping them.
 * Null when the element sits outside a table, or when the grid nesting is
 * malformed.
 * @param element - The selected source element
 * @returns The table context, or null
 */
function tableContextOf(element: Element): TableContext | null {
  const tag = element.tagName.toLowerCase();
  if (tag === "fp-table") {
    return { table: element, row: null, cell: null };
  }
  if (tag === "fp-row") {
    const table = parentWithTag(element, "fp-table");
    return table ? { table, row: element, cell: null } : null;
  }
  if (tag === "fp-cell") {
    const row = parentWithTag(element, "fp-row");
    const table = row ? parentWithTag(row, "fp-table") : null;
    return row && table ? { table, row, cell: element } : null;
  }
  const enclosing = element.parentElement;
  return enclosing ? tableContextOf(enclosing) : null;
}

/**
 * An element's parent when it carries a given tag, else null.
 * @param element - The child element
 * @param tagName - The lowercased tag name the parent must carry
 * @returns The parent, or null
 */
function parentWithTag(element: Element, tagName: string): Element | null {
  const parent = element.parentElement;
  return parent !== null && parent.tagName.toLowerCase() === tagName
    ? parent
    : null;
}

/**
 * How many grid columns a static table covers: its widest row measured by
 * spans (rowspan carryover included), or its `<fp-column>` ruler when that is
 * longer.
 * @param table - The `<fp-table>` element
 * @returns The table's grid width, at least one
 */
function tableGridWidth(table: Element): number {
  const widest = tableRowLayouts(table).reduce(
    (max, layout) => Math.max(max, layout.width),
    0,
  );
  return Math.max(widest, directChildren(table, "fp-column").length, 1);
}

/**
 * How many grid columns a cell covers: its numeric span, or one. A `span="all"`
 * cell counts as one, matching the renderer - it stretches to whatever width
 * the other rows establish rather than occupying columns of its own.
 * @param cell - The `<fp-cell>` element
 * @returns The cell's grid span
 */
function gridSpanOf(cell: Element): number {
  const { span } = parseTableColumn(cell);
  return typeof span === "number" ? span : 1;
}

/**
 * How many rows a cell covers: its numeric rowspan, or one.
 * @param cell - The `<fp-cell>` element
 * @returns The cell's row span
 */
function rowspanOf(cell: Element): number {
  return parseTableColumn(cell).rowspan;
}

/**
 * Writes a cell's `span`, dropping the attribute at a single column. A
 * `span="all"` cell never reaches here - every caller refuses it first.
 * @param cell - The `<fp-cell>` element
 * @param span - The column count to declare
 */
function setSpan(cell: Element, span: number): void {
  if (span > 1) {
    cell.setAttribute("span", String(span));
  } else {
    cell.removeAttribute("span");
  }
}

/**
 * Writes a cell's `rowspan`, dropping the attribute at a single row.
 * @param cell - The `<fp-cell>` element
 * @param rowspan - The row count to declare
 */
function setRowspan(cell: Element, rowspan: number): void {
  if (rowspan > 1) {
    cell.setAttribute("rowspan", String(rowspan));
  } else {
    cell.removeAttribute("rowspan");
  }
}

/** One cell's grid placement inside a static table. */
interface CellSlot {
  cell: Element;
  /** The zero-based grid column the cell starts at. */
  start: number;
  /** How many grid columns the cell covers. */
  span: number;
  /** How many rows the cell covers. */
  rowspan: number;
}

/** One row's grid layout, rowspan carryover from the rows above resolved. */
interface RowLayout {
  row: Element;
  /** The row's own cells at their grid columns, in order. */
  slots: CellSlot[];
  /** Cells from earlier rows still covering grid columns of this row. */
  carryover: CellSlot[];
  /** The grid columns the row covers, carryover included. */
  width: number;
}

/**
 * Lays a static table's rows onto its grid. A cell spanning rows keeps
 * covering its grid columns on the rows below it, so those rows' own cells
 * start past the covered columns instead of sliding underneath them - matching
 * the renderer's layout.
 * @param table - The `<fp-table>` element
 * @returns Each row's layout, in row order
 */
function tableRowLayouts(table: Element): RowLayout[] {
  let active: { slot: CellSlot; until: number }[] = [];
  return directChildren(table, "fp-row").map((row, rowIndex) => {
    const carrying = active.filter((entry) => entry.until > rowIndex);
    const carryover = carrying.map((entry) => entry.slot);
    const occupied = new Set(
      carryover.flatMap((slot) =>
        Array.from(
          { length: slot.span },
          (_unused, offset) => slot.start + offset,
        ),
      ),
    );
    const slots: CellSlot[] = [];
    let position = 0;
    for (const cell of directChildren(row, "fp-cell")) {
      while (occupied.has(position)) {
        position += 1;
      }
      const slot: CellSlot = {
        cell,
        start: position,
        span: gridSpanOf(cell),
        rowspan: rowspanOf(cell),
      };
      slots.push(slot);
      position += slot.span;
    }
    const width = carryover.reduce(
      (max, slot) => Math.max(max, slot.start + slot.span),
      position,
    );
    active = [
      ...carrying,
      ...slots
        .filter((slot) => slot.rowspan > 1)
        .map((slot) => ({ slot, until: rowIndex + slot.rowspan })),
    ];
    return { row, slots, carryover, width };
  });
}

/**
 * A row's grid layout, resolved through its enclosing table so rowspan
 * carryover from earlier rows is accounted for.
 * @param row - The `<fp-row>` element
 * @returns The row's layout, or null when the row sits outside a table
 */
function rowLayoutOf(row: Element): RowLayout | null {
  const table = parentWithTag(row, "fp-table");
  if (table === null) {
    return null;
  }
  return tableRowLayouts(table).find((layout) => layout.row === row) ?? null;
}

/**
 * The grid column a cell starts at, rowspan carryover from earlier rows
 * accounted for.
 * @param cell - The `<fp-cell>` element
 * @returns The cell's zero-based start column
 */
function gridStartOf(cell: Element): number {
  const row = cell.parentElement;
  const layout = row === null ? null : rowLayoutOf(row);
  return layout?.slots.find((slot) => slot.cell === cell)?.start ?? 0;
}

/**
 * Removes a row emptied of its last cell, shrinking the rowspan of every
 * merged cell still covering it so the rows below keep their alignment. A row
 * that still holds a cell is left alone.
 * @param row - The `<fp-row>` element that may have been emptied
 */
function collapseEmptiedRow(row: Element): void {
  if (directChildren(row, "fp-cell").length > 0) {
    return;
  }
  const layout = rowLayoutOf(row);
  layout?.carryover.forEach((slot) =>
    setRowspan(slot.cell, rowspanOf(slot.cell) - 1),
  );
  row.remove();
}

/**
 * Inserts an empty cell into one row at a grid column: before the cell starting
 * there, appended when the target sits exactly at the row's end, or absorbed by
 * growing a cell whose span straddles the target. A narrower row, or one
 * holding a `span="all"` cell, is left untouched. A target covered by a merged
 * cell carried over from an earlier row is skipped too - the owning row grows
 * that cell instead. The new cell wears the look of the cell it lands against,
 * so it sits inside the row's rules rather than beside them.
 * @param doc - The parsed source document
 * @param layout - The row's grid layout, captured before any row was mutated
 * @param target - The zero-based grid column the new cell lands at
 * @returns The inserted cell, or null when the row was left untouched
 */
function insertCellAtColumn(
  doc: Document,
  layout: RowLayout,
  target: number,
): Element | null {
  if (layout.slots.some((slot) => parseTableColumn(slot.cell).span === "all")) {
    return null;
  }
  if (
    layout.carryover.some(
      (slot) => target >= slot.start && target < slot.start + slot.span,
    )
  ) {
    return null;
  }
  for (const slot of layout.slots) {
    if (target <= slot.start) {
      const inserted = emptyTableCell(doc, slot.cell);
      slot.cell.before(inserted);
      return inserted;
    }
    if (target < slot.start + slot.span) {
      setSpan(slot.cell, slot.span + 1);
      return null;
    }
  }
  const end = layout.slots.reduce(
    (max, slot) => Math.max(max, slot.start + slot.span),
    0,
  );
  if (target > end) {
    return null;
  }
  const last = layout.slots[layout.slots.length - 1]?.cell ?? null;
  const inserted = emptyTableCell(doc, last);
  layout.row.append(inserted);
  return inserted;
}

/**
 * The cell covering one grid column of a row - the row's own, or a merged cell
 * carried over from an earlier row - so a new cell landing in that column can
 * be modelled on the one already there.
 * @param row - The `<fp-row>` element
 * @param target - The zero-based grid column
 * @returns The cell covering the column, or null when the row is too narrow
 */
function cellAtColumn(row: Element, target: number): Element | null {
  const layout = rowLayoutOf(row);
  const covering = [
    ...(layout?.slots ?? []),
    ...(layout?.carryover ?? []),
  ].find((slot) => target >= slot.start && target < slot.start + slot.span);
  return covering?.cell ?? null;
}

/**
 * Gives a declaration just added to a grid an equal share of the width its
 * siblings already declare, taken proportionally from them, so the grid still
 * sums to the total it was authored with. A list that is not entirely
 * percentage-sized has no total to divide, so the new declaration is left unsized
 * and takes whatever the layout gives it.
 * @param parent - The `<fp-row>` or `<fp-table>` holding the declarations
 * @param tagName - The lowercased tag of the declarations
 * @param added - The declaration just inserted, already in place
 * @returns Nothing; the width attributes are rewritten in place
 */
function shareDeclaredWidth(
  parent: Element,
  tagName: string,
  added: Element,
): void {
  const siblings = directChildren(parent, tagName).filter(
    (element) => element !== added,
  );
  const total = percentageTotal(siblings);
  if (total === null) {
    added.removeAttribute("width");
    return;
  }
  const share = total / (siblings.length + 1);
  rescaleColumnWidths(siblings, total - share);
  added.setAttribute("width", `${share.toFixed(2)}%`);
}

/**
 * Removes the cell occupying one grid column from a row, or shrinks a cell
 * spanning across it by one (dropping the `span` attribute when it reaches a
 * single column). A narrower row, one holding a `span="all"` cell, or one whose
 * target column is covered by a merged cell from an earlier row (the owning row
 * shrinks that cell) is left untouched.
 * @param layout - The row's grid layout, captured before any row was mutated
 * @param target - The zero-based grid column to remove
 */
function removeCellAtColumn(layout: RowLayout, target: number): void {
  if (layout.slots.some((slot) => parseTableColumn(slot.cell).span === "all")) {
    return;
  }
  const covering = layout.slots.find(
    (slot) => target >= slot.start && target < slot.start + slot.span,
  );
  if (!covering) {
    return;
  }
  if (covering.span > 1) {
    setSpan(covering.cell, covering.span - 1);
  } else {
    covering.cell.remove();
  }
}

/**
 * Inserts an `<fp-column>` into a table's positional ruler at a grid column, so
 * the widths and styles of the following columns do not shift onto the wrong
 * cells, and gives it a share of whatever width the ruler already declares. A
 * table without a ruler stays without one, and a target beyond the ruler's reach
 * leaves it alone - the ruler only describes the columns it covers.
 * @param doc - The parsed source document
 * @param table - The `<fp-table>` element
 * @param target - The zero-based grid column the new declaration lands at
 */
function insertRulerColumn(
  doc: Document,
  table: Element,
  target: number,
): void {
  const ruler = directChildren(table, "fp-column");
  if (ruler.length === 0 || target > ruler.length) {
    return;
  }
  const column = doc.createElement("fp-column");
  if (target === ruler.length) {
    ruler[ruler.length - 1].after("\n", column);
  } else {
    ruler[target].before(column, "\n");
  }
  shareDeclaredWidth(table, "fp-column", column);
}

/**
 * `<fp-cell>` attributes a new empty cell never copies from the one it is
 * modelled on: what that cell spans and when it shows are its own placement, and
 * a copied span would push the grid out of shape.
 */
const CELL_UNCOPIED_ATTRIBUTES = ["span", "rowspan", FP_SHOW_WHEN_ATTRIBUTE];

/**
 * An empty authored table cell, wearing the look of the cell it is modelled on -
 * its width, padding, border, alignment and type - since these grids declare
 * their rules on the cells themselves and a bare cell would land outside them.
 * The cell owns its text rather than nesting blocks: it stays childless, and
 * the text-edit path resolves the cell itself (renderedTextEdit.ts).
 * @param doc - The parsed source document
 * @param model - The cell whose look is copied, or null for a bare cell
 * @returns The empty `<fp-cell>` element
 */
function emptyTableCell(doc: Document, model: Element | null): Element {
  const cell = doc.createElement("fp-cell");
  if (model) {
    Array.from(model.attributes)
      .filter((attribute) => !CELL_UNCOPIED_ATTRIBUTES.includes(attribute.name))
      .forEach((attribute) =>
        cell.setAttribute(attribute.name, attribute.value),
      );
  }
  return cell;
}

/**
 * Resolves a positional node id to its source element. Top-level ids are "nK"
 * (index among body children); nested repeater ids are dotted
 * child-element-index paths from the top-level block (e.g. "n2.1.0"), matching
 * the ids the renderer stamps.
 * @param doc - The parsed source document
 * @param nodeId - Positional node id ("nK" or "nK.i.j...")
 * @returns The element, or null when the id doesn't resolve
 */
export function resolveBlock(
  doc: Document,
  nodeId: string,
): HTMLElement | null {
  const match = /^n(\d+(?:\.\d+)*)$/.exec(nodeId);
  if (!match) {
    return null;
  }
  const indices = match[1].split(".").map(Number);
  const element = indices.reduce<Element | null>(
    (current, index) => current?.children[index] ?? null,
    doc.body,
  );
  return element instanceof HTMLElement ? element : null;
}

/**
 * Computes the positional node id of a source element: the dotted child-element
 * path from the body, matching the renderer's stamping.
 * @param doc - The parsed source document
 * @param block - An element inside the body
 * @returns The positional node id ("nK" or "nK.i.j...")
 */
export function blockIdOf(doc: Document, block: Element): string {
  const indices: number[] = [];
  for (
    let element: Element | null = block;
    element !== null && element.parentElement !== null;
    element = element.parentElement
  ) {
    const parent = element.parentElement;
    indices.unshift(Array.prototype.indexOf.call(parent.children, element));
    if (parent === doc.body) {
      break;
    }
  }
  return `n${indices.join(".")}`;
}

/**
 * Interleaves elements with newline text nodes so consecutive inserted blocks
 * keep the stored HTML readable (matching the authored one-block-per-line
 * style).
 * @param elements - The block elements to interleave
 * @returns The elements with a "\n" between each pair
 */
function interleaveWithNewlines(
  elements: readonly Element[],
): (Node | string)[] {
  return elements.flatMap((element, index): (Node | string)[] =>
    index === 0 ? [element] : ["\n", element],
  );
}

/**
 * Inserts blocks at the end of the document flow: before the repeatable
 * footer when the document has one, otherwise appended to the body.
 * @param doc - The parsed source document
 * @param blocks - The block elements to insert, in order
 */
function insertBeforeFooterOrAppend(
  doc: Document,
  blocks: readonly Element[],
): void {
  const footer = findRepeatableFooter(doc);
  const nodes = interleaveWithNewlines(blocks);
  if (footer) {
    footer.before(...nodes, "\n");
  } else {
    doc.body.append(...nodes, "\n");
  }
}

/**
 * Tags whose `source` binds a quote field, so the block is named after the
 * field rather than after its tag. `fp-text` belongs here because a bound
 * `<fp-text source="terms-of-service">` is the fp-only form of the markdown
 * block it replaces.
 */
const FIELD_BOUND_TAGS = new Set(["fp-field", "fp-markdown", "fp-text"]);

/** Friendly display names for block tags shown to end users. */
const TAG_DISPLAY_NAMES: Record<string, string> = {
  blockquote: "Quote text",
  div: "Section",
  figcaption: "Caption",
  figure: "Figure",
  footer: "Footer",
  h1: "Heading",
  h2: "Heading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  h6: "Heading",
  header: "Header",
  hr: "Divider",
  img: "Image",
  ol: "List",
  p: "Text",
  section: "Section",
  table: "Table",
  ul: "List",
  "fp-breaks": "Quantity breaks",
  "fp-cell": "Cell",
  "fp-column": "Column",
  "fp-columns": "Columns",
  "fp-divider": "Divider",
  "fp-field": "Field",
  "fp-footer": "Footer",
  "fp-header": "Header",
  "fp-image": "Image",
  "fp-lines": "Line items",
  "fp-markdown": "Text",
  "fp-page-break": "Page break",
  "fp-page-count": "Page count",
  "fp-page-frame": "Page frame",
  "fp-page-number": "Page number",
  "fp-row": "Row",
  "fp-section-heading": "Section heading",
  "fp-section-notes": "Section notes",
  "fp-table": "Table",
  "fp-text": "Text",
};

/**
 * Builds the short label shown on the selection chip and inspector header,
 * in end-user terms (no tag names or field keys): field-bound blocks show the
 * field's friendly label, everything else a friendly name for its tag.
 * @param block - The source block element
 * @returns The user-facing block label
 */
function describeBlock(block: Element): string {
  const tag = block.tagName.toLowerCase();
  const displayName = TAG_DISPLAY_NAMES[tag] ?? "Block";
  if (tag === "fp-cell" && isColumnsColumn(block)) {
    return "Column";
  }
  if (FIELD_BOUND_TAGS.has(tag)) {
    return quoteFieldLabel(block.getAttribute("source")) ?? displayName;
  }
  if (tag === "fp-lines") {
    return linesDisplayName(block.getAttribute("filter")) ?? displayName;
  }
  if (tag === "fp-table" && repeatsQuoteLines(block)) {
    return "Item table";
  }
  return displayName;
}

/**
 * Whether an `<fp-table>` binds its rows to the quote's lines rather than to
 * authored `<fp-row>` children, mirroring the renderer's own static test.
 * @param table - The `<fp-table>` source element
 * @returns True when the table repeats quote lines
 */
function repeatsQuoteLines(table: Element): boolean {
  if (directChildren(table, "fp-row").length > 0) {
    return false;
  }
  return (
    table.hasAttribute("scope") || directChildren(table, "fp-column").length > 0
  );
}

/** What each `fp-lines` filter repeats, in the author's terms. */
const LINES_FILTER_DISPLAY_NAMES: Record<FpLinesFilter, string> = {
  items: "Item rows",
  discounts: "Discount rows",
  notes: "Note rows",
  sections: "Section rows",
  all: "All rows",
};

/**
 * The friendly name for an `fp-lines` filter attribute.
 * @param filter - The raw `filter` attribute value, or null when unset
 * @returns The display name, or null when the filter is unset or unknown
 */
function linesDisplayName(filter: string | null): string | null {
  const known = FP_LINES_FILTERS.find((candidate) => candidate === filter);
  return known ? LINES_FILTER_DISPLAY_NAMES[known] : null;
}

/**
 * Finds the authored repeatable footer block, if the document has one.
 * @param doc - The parsed source document
 * @returns The footer element, or null
 */
function findRepeatableFooter(doc: Document): Element | null {
  return findRepeatableChrome(doc, "footer");
}

/**
 * Whether the document already authors the given repeatable page chrome, so
 * the palette can withhold a second header or footer it would silently drop.
 * @param html - The stored document HTML
 * @param kind - Which chrome block to look for
 * @returns True when the document has that block, in either authored form
 */
export function hasRepeatableChrome(
  html: string,
  kind: "header" | "footer",
): boolean {
  return findRepeatableChrome(parseDocumentHtml(html), kind) !== null;
}

/**
 * Whether the document already carries a page frame. The renderer draws the
 * first one only, so the palette offers a second one disabled.
 * @param html - The stored document HTML
 * @returns True when an `<fp-page-frame>` is authored anywhere in the document
 */
export function hasPageFrame(html: string): boolean {
  return parseDocumentHtml(html).querySelector("fp-page-frame") !== null;
}

/**
 * Finds the authored repeatable chrome block of the given kind. The selector
 * mirrors the renderer's `extractRepeatable`, so the editor and the render
 * agree on which element leaves the flow.
 * @param doc - The parsed source document
 * @param kind - Which chrome block to find
 * @returns The element, or null when the document has none
 */
function findRepeatableChrome(
  doc: Document,
  kind: "header" | "footer",
): Element | null {
  return doc.body.querySelector(
    `${kind}[data-fp-repeat="each-page"], fp-${kind}`,
  );
}

/**
 * Whether an element is the authored repeatable footer.
 * @param element - The element to test
 * @returns True for `<fp-footer>` or `<footer data-fp-repeat="each-page">`
 */
function isRepeatableFooter(element: Element): boolean {
  return isRepeatableChrome(element, "footer");
}

/**
 * Whether an element is the authored repeatable header or footer, both of
 * which are lifted out of the document flow at render time.
 * @param element - The element to test
 * @returns True for either page-chrome block, in either authored form
 */
function isRepeatableBlock(element: Element): boolean {
  return (
    isRepeatableChrome(element, "header") ||
    isRepeatableChrome(element, "footer")
  );
}

/**
 * Whether an element is one kind of repeatable page chrome, in either authored
 * form: the fp-only `<fp-header>`/`<fp-footer>`, or the legacy HTML tag marked
 * `data-fp-repeat="each-page"`.
 * @param element - The element to test
 * @param kind - Which chrome block to match
 * @returns True when the element is that page-chrome block
 */
function isRepeatableChrome(
  element: Element,
  kind: "header" | "footer",
): boolean {
  const tag = element.tagName.toLowerCase();
  if (tag === `fp-${kind}`) {
    return true;
  }
  return tag === kind && element.getAttribute("data-fp-repeat") === "each-page";
}

/**
 * Whether an element is the repeatable header or footer with nothing in it.
 * @param element - The element to test
 * @returns True for an empty repeatable chrome block
 */
function isEmptyRepeatableBlock(element: Element): boolean {
  return (
    isRepeatableBlock(element) &&
    element.children.length === 0 &&
    (element.textContent ?? "").trim() === ""
  );
}

/**
 * Whether an element is the authored repeatable header or footer or sits
 * inside one, and so renders once per page carrying it.
 * @param element - The element to test
 * @returns True when the element renders on every page carrying it
 */
function insideRepeatableBlock(element: Element): boolean {
  for (
    let current: Element | null = element;
    current !== null;
    current = current.parentElement
  ) {
    if (isRepeatableBlock(current)) {
      return true;
    }
  }
  return false;
}

/**
 * Converts a camelCased style property to its CSS kebab-case name.
 * @param property - The camelCased property name
 * @returns The kebab-case CSS property name
 */
export function toKebabCase(property: string): string {
  return property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}
